import React, { useCallback, useEffect, useState, createElement } from 'react';
import { DollarSignIcon, SearchIcon, AlertCircleIcon, ClockIcon, FileTextIcon, PlusIcon, EditIcon, TrashIcon, UserIcon, CheckCircleIcon, PercentIcon, SettingsIcon, CreditCardIcon, DownloadIcon, FileMinusIcon, ArrowRightLeftIcon, CalculatorIcon, BanIcon, UndoIcon, FilterIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Invoice, CreditNote } from '../types';
import { useApp } from '../context/AppContext';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import jsPDF from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
type TabType = 'facturas' | 'notas-credito' | 'aplicar-nc' | 'historial-nc';
type FormData = {
  numeroFactura: string;
  farmacia: string;
  vendedor: string;
  vendedorId: string;
  fechaEmision: string;
  fechaVencimiento: string;
  subTotal: string;
  itbis: string;
  monto: string;
  saldoPendiente: string;
  estado: Invoice['estado'];
  ppPct: string;
};
type NCFormData = {
  numero: string;
  farmacia: string;
  vendedor: string;
  vendedorId: string;
  monto: string;
  notas: string;
};
const emptyForm: FormData = {
  numeroFactura: '',
  farmacia: '',
  vendedor: '',
  vendedorId: '',
  fechaEmision: new Date().toISOString().split('T')[0],
  fechaVencimiento: '',
  subTotal: '',
  itbis: '',
  monto: '',
  saldoPendiente: '',
  estado: 'pendiente',
  ppPct: ''
};
const emptyNCForm: NCFormData = {
  numero: '',
  farmacia: '',
  vendedor: '',
  vendedorId: '',
  monto: '',
  notas: ''
};
// ── PDF Receipt ───────────────────────────────────────────────────────────────
const LOGO_URL = '/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
export async function downloadReciboPago(farmacia: string, invoicesPaid: {
  numeroFactura: string;
  monto: number;
  abono: number;
  descuento?: number;
}[], totalPagado: number, metodoPago: string) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a5'
  });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 10;
  const mr = 10;
  const fmtNum = (n: number) => new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
  const fmtDate = (d: Date) => d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const logoBase64 = await getLogoBase64();
  if (logoBase64) doc.addImage(logoBase64, 'PNG', ml, 4, 20, 20);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('Miladys Rodriguez & Aso.', ml + 24, 12);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Av. Antonio Guzmán Nº 102 (Altos) • Tels. 809-247-2104 • 809-247-1006', ml + 24, 18);
  doc.text('Fax: 809-247-5098 • Santiago, República Dominicana  •  RNC 130-329291', ml + 24, 23);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('Fecha:', pw - mr - 44, 12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(fmtDate(new Date()), pw - mr - 20, 12);
  doc.setFillColor(220, 235, 255);
  doc.setDrawColor(41, 82, 196);
  doc.setLineWidth(0.6);
  doc.rect(pw - mr - 46, 14, 46, 10, 'FD');
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('RD$ ' + fmtNum(totalPagado), pw - mr - 23, 21, {
    align: 'center'
  });
  const receiptNum = Math.floor(Math.random() * 90000 + 10000).toString();
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(200, 30, 30);
  doc.text(receiptNum, pw - mr, 32, {
    align: 'right'
  });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('NUMERO DE RECIBO  M', ml, 30);
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.9);
  doc.line(ml, 31.5, pw - mr, 31.5);
  doc.setLineWidth(0.3);
  doc.line(ml, 33, pw - mr, 33);
  let y = 40;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('He recibido de', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(farmacia, ml + 34, y);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.line(ml + 33, y + 1, pw - mr, y + 1);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('El valor de', ml, y);
  doc.line(ml + 24, y + 1, pw - mr, y + 1);
  y += 7;
  doc.setFontSize(7.5);
  doc.text('Para ser abonado a su cuenta MILADYS RODRIGUEZ & ASOCIADOS, en la siguiente forma:', ml, y);
  y += 5;
  const cols = ['FACTURA Nº', 'FECHA', 'VALOR', 'CRÉDITO', 'DESCUENTO', 'MONTO'];
  const colW = [35, 28, 32, 30, 30, 35];
  const tableW = 190;
  const rowH = 7.5;
  doc.setFillColor(13, 27, 70);
  doc.rect(ml, y, tableW, rowH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  let x = ml;
  cols.forEach((col, i) => {
    doc.text(col, x + colW[i] / 2, y + rowH - 2.2, {
      align: 'center'
    });
    x += colW[i];
  });
  y += rowH;
  const totalRows = Math.max(invoicesPaid.length, 5);
  for (let i = 0; i < totalRows; i++) {
    const isEven = i % 2 === 0;
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, tableW, rowH, 'F');
    doc.setDrawColor(180, 200, 220);
    doc.setLineWidth(0.2);
    doc.rect(ml, y, tableW, rowH, 'S');
    if (i < invoicesPaid.length) {
      const inv = invoicesPaid[i];
      let cx = ml;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(inv.numeroFactura, cx + colW[0] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[0];
      doc.setFont('helvetica', 'normal');
      doc.text(fmtDate(new Date()), cx + colW[1] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[1];
      doc.text(fmtNum(inv.monto), cx + colW[2] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[2];
      doc.text(fmtNum(inv.abono), cx + colW[3] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[3];
      if (inv.descuento && inv.descuento > 0) {
        doc.setTextColor(5, 120, 60);
        doc.text(fmtNum(inv.descuento), cx + colW[4] / 2, y + rowH - 2.2, {
          align: 'center'
        });
        doc.setTextColor(0, 0, 0);
      }
      cx += colW[4];
      const neto = inv.abono - (inv.descuento || 0);
      doc.setFont('helvetica', 'bold');
      doc.text(fmtNum(neto > 0 ? neto : inv.abono), cx + colW[5] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      doc.setFont('helvetica', 'normal');
    }
    y += rowH;
  }
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.5);
  doc.line(ml, y, ml + tableW, y);
  const subTotalVal = Math.round(totalPagado / 1.18 * 100) / 100;
  const itbisVal = Math.round((totalPagado - subTotalVal) * 100) / 100;
  const summaryRowH = 7.5;
  y += 2; // spacing before summary
  // Sub Total row
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, tableW, summaryRowH, 'F');
  doc.setDrawColor(180, 200, 220);
  doc.setLineWidth(0.15);
  doc.rect(ml, y, tableW, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('SUB TOTAL:', ml + tableW - 58, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RD$ ' + fmtNum(subTotalVal), ml + tableW - 2, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // ITBIS row
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, tableW, summaryRowH, 'F');
  doc.setDrawColor(180, 200, 220);
  doc.setLineWidth(0.15);
  doc.rect(ml, y, tableW, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('ITBIS 18%:', ml + tableW - 58, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RD$ ' + fmtNum(itbisVal), ml + tableW - 2, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // TOTAL row
  const totalSummaryH = 9;
  doc.setFillColor(220, 235, 255);
  doc.rect(ml, y, tableW, totalSummaryH, 'F');
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.4);
  doc.rect(ml, y, tableW, totalSummaryH, 'S');
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('TOTAL:', ml + tableW - 58, y + totalSummaryH / 2 + 1, {
    align: 'left'
  });
  doc.text('RD$ ' + fmtNum(totalPagado), ml + tableW - 2, y + totalSummaryH / 2 + 1, {
    align: 'right'
  });
  y += totalSummaryH;
  y += 9;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.text('Efectivo', ml, y);
  doc.line(ml + 16, y + 1, ml + 56, y + 1);
  if (metodoPago === 'efectivo') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text(fmtNum(totalPagado), ml + 17, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Cheque Nº', ml + 60, y);
  doc.line(ml + 76, y + 1, ml + 110, y + 1);
  if (metodoPago === 'cheque') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('✓', ml + 77, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Banco', ml + 112, y);
  doc.line(ml + 122, y + 1, ml + 152, y + 1);
  if (metodoPago === 'transferencia') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('Transferencia', ml + 123, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Futurista', ml + 154, y);
  doc.line(ml + 165, y + 1, pw - mr, y + 1);
  y += 14;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + 65, y);
  doc.line(pw - mr - 65, y, pw - mr, y);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Conforme Cliente', ml + 32, y + 5, {
    align: 'center'
  });
  doc.text('Recibido Conforme', pw - mr - 32, y + 5, {
    align: 'center'
  });
  doc.setFillColor(13, 27, 70);
  doc.rect(0, ph - 9, pw, 9, 'F');
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(123, 163, 216);
  doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', pw / 2, ph - 3.5, {
    align: 'center'
  });
  doc.save(`recibo-${farmacia.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
export function CuentasCobrarPage() {
  const {
    clients,
    users,
    orders
  } = useApp();
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const canSeeDiscount = isAdmin || user?.permisos?.descuento_pronto_pago === true || user?.permisos?.descuento_pronto_pago === undefined;
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('facturas');
  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDiscountConfigOpen, setIsDiscountConfigOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [facturaError, setFacturaError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Credit Notes state
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  // ── Name normalization helpers (used across tabs) ──────────────────────────
  // Aggressive normalization: lowercase, collapse whitespace, strip punctuation & accents
  const normalizeName = (s: string) => s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
  .replace(/[^a-z0-9\s]/g, '') // strip punctuation
  .replace(/\s+/g, ' ') // collapse whitespace
  .trim();
  // Looser match: checks if two names refer to the same farmacia
  const farmaciaMatch = (a: string, b: string) => {
    if (!a || !b) return false;
    const na = normalizeName(a);
    const nb = normalizeName(b);
    if (na === nb) return true;
    // Fallback: one contains the other (handles partial name differences)
    if (na.length > 3 && nb.length > 3) {
      if (na.includes(nb) || nb.includes(na)) return true;
    }
    // Fallback: compare first N significant characters (handles trailing suffixes like "SRL" vs "EIRL")
    const wordsA = na.split(' ').filter(w => w.length > 1);
    const wordsB = nb.split(' ').filter(w => w.length > 1);
    if (wordsA.length >= 2 && wordsB.length >= 2) {
      // If the first 2 significant words match, consider it a match
      if (wordsA[0] === wordsB[0] && wordsA[1] === wordsB[1]) return true;
    }
    return false;
  };
  // Derived lists — must be after creditNotes useState
  // Collect all farmacia names from clients, invoices, and credit notes
  const allFarmaciaNames = [...clients.map(c => c.nombre), ...invoices.filter(inv => inv.farmacia).map(inv => inv.farmacia), ...creditNotes.filter(nc => nc.farmacia).map(nc => nc.farmacia)];

  // Deduplicate using normalizeName to avoid near-duplicates
  const seenNormalized = new Map<string, string>();
  allFarmaciaNames.forEach(name => {
    const norm = normalizeName(name);
    if (!seenNormalized.has(norm)) {
      seenNormalized.set(norm, name);
    }
  });
  const FARMACIAS = [...seenNormalized.values()].sort();
  const VENDEDORES = users.filter(u => u.activo && u.rolNombre === 'Vendedor').map(u => u.nombre);
  const [isNCModalOpen, setIsNCModalOpen] = useState(false);
  const [isNCDeleteModalOpen, setIsNCDeleteModalOpen] = useState(false);
  const [editingNC, setEditingNC] = useState<CreditNote | null>(null);
  const [ncFormData, setNCFormData] = useState<NCFormData>(emptyNCForm);
  const [ncError, setNCError] = useState('');
  const [ncPage, setNCPage] = useState(1);
  const [ncSearchQuery, setNCSearchQuery] = useState('');
  // Estado de Cuenta state
  const [showEstadoCuenta, setShowEstadoCuenta] = useState(false);
  const [estadoCuentaClient, setEstadoCuentaClient] = useState('');
  const [isGeneratingEstadoCuenta, setIsGeneratingEstadoCuenta] = useState(false);
  // Application state
  const [appFarmacia, setAppFarmacia] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedNCIds, setSelectedNCIds] = useState<string[]>([]);
  const [isApplying, setIsApplying] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  // NC Applications history state
  const [ncApplications, setNcApplications] = useState<any[]>([]);
  const [anularNCApp, setAnularNCApp] = useState<any | null>(null);
  const [isAnulandoNC, setIsAnulandoNC] = useState(false);
  const [ncAppPage, setNcAppPage] = useState(1);
  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentClient, setPaymentClient] = useState('');
  const [paymentAmounts, setPaymentAmounts] = useState<Record<string, number>>({});
  const [paymentMetodo, setPaymentMetodo] = useState('efectivo');
  const [paymentDone, setPaymentDone] = useState(false);
  const [lastPaymentData, setLastPaymentData] = useState<{
    farmacia: string;
    invoices: {
      numeroFactura: string;
      monto: number;
      abono: number;
    }[];
    total: number;
  } | null>(null);
  const [isDownloadingRecibo, setIsDownloadingRecibo] = useState(false);
  const [isCotizacionModalOpen, setIsCotizacionModalOpen] = useState(false);
  const [cotizacionSearch, setCotizacionSearch] = useState('');
  const [cotizaciones, setCotizaciones] = useState<any[]>([]);
  const [isLoadingCotizaciones, setIsLoadingCotizaciones] = useState(false);
  // Discount config
  const [discountPct, setDiscountPct] = useState(() => parseFloat(localStorage.getItem('mr_discount_pct') || '5'));
  const [discountDays, setDiscountDays] = useState(() => parseInt(localStorage.getItem('mr_discount_days') || '60', 10));
  const [discountConfigDraft, setDiscountConfigDraft] = useState(() => ({
    pct: parseFloat(localStorage.getItem('mr_discount_pct') || '5'),
    days: parseInt(localStorage.getItem('mr_discount_days') || '60', 10)
  }));
  // Load cotizaciones from DB when modal opens
  useEffect(() => {
    if (!isCotizacionModalOpen) return;
    const loadCotizaciones = async () => {
      setIsLoadingCotizaciones(true);
      try {
        const {
          data
        } = await supabaseAdmin.from('cotizaciones').select('id,numero_cotizacion,farmacia,vendedor,fecha,items,total').order('fecha', {
          ascending: false
        }).limit(100);
        if (data) setCotizaciones(data);
      } catch (err) {
        console.error('Error loading cotizaciones:', err);
      } finally {
        setIsLoadingCotizaciones(false);
      }
    };
    loadCotizaciones();
  }, [isCotizacionModalOpen]);
  useEffect(() => {
    const loadDiscountFromDB = async () => {
      try {
        const {
          data
        } = await supabaseAdmin.from('app_settings').select('key, value').in('key', ['discount_pct', 'discount_days']);
        if (data && data.length > 0) {
          const pctRow = data.find((r: any) => r.key === 'discount_pct');
          const daysRow = data.find((r: any) => r.key === 'discount_days');
          const pct = pctRow ? parseFloat(pctRow.value) : 5;
          const days = daysRow ? parseInt(daysRow.value, 10) : 60;
          setDiscountPct(pct);
          setDiscountDays(days);
          setDiscountConfigDraft({
            pct,
            days
          });
          localStorage.setItem('mr_discount_pct', String(pct));
          localStorage.setItem('mr_discount_days', String(days));
        }
      } catch {}
    };
    loadDiscountFromDB();
  }, []);
  const saveDiscountConfig = async (pct: number, days: number) => {
    try {
      await supabaseAdmin.from('app_settings').upsert([{
        key: 'discount_pct',
        value: String(pct),
        updated_at: new Date().toISOString()
      }, {
        key: 'discount_days',
        value: String(days),
        updated_at: new Date().toISOString()
      }]);
    } catch {}
    localStorage.setItem('mr_discount_pct', String(pct));
    localStorage.setItem('mr_discount_days', String(days));
    setDiscountPct(pct);
    setDiscountDays(days);
    window.dispatchEvent(new CustomEvent('discount-config-updated', {
      detail: {
        pct,
        days
      }
    }));
  };
  const mapInvoice = (row: any): Invoice => {
    const fechaVencimiento = new Date(row.fecha_vencimiento);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let estado: Invoice['estado'] = row.estado;
    if (estado !== 'pagada' && fechaVencimiento < today && row.saldo_pendiente > 0) estado = 'vencida';
    return {
      id: row.id,
      numeroFactura: row.numero_factura,
      farmacia: row.farmacia,
      vendedor: row.vendedor,
      vendedorId: row.vendedor_id || undefined,
      fechaEmision: new Date(row.fecha_emision),
      fechaVencimiento,
      subTotal: row.sub_total || 0,
      itbis: row.itbis || 0,
      monto: row.monto,
      saldoPendiente: row.saldo_pendiente,
      estado,
      ppPct: row.pp_pct != null ? Number(row.pp_pct) : null
    };
  };
  const mapCreditNote = (row: any): CreditNote => ({
    id: row.id,
    numero: row.numero,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    vendedorId: row.vendedor_id || undefined,
    monto: row.monto,
    montoDisponible: row.monto_disponible,
    facturaAplicada: row.factura_aplicada || null,
    estado: row.estado,
    fechaCreacion: new Date(row.fecha_creacion),
    notas: row.notas || ''
  });
  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,fecha_vencimiento,sub_total,itbis,monto,saldo_pendiente,estado,pp_pct').not('estado', 'eq', 'anulada').gte('fecha_emision', new Date(Date.now() - 180 * 864e5).toISOString().split('T')[0]).order('fecha_emision', {
        ascending: false
      }).limit(400);
      if (error) {
        console.error('[CuentasCobrar] Error cargando facturas:', error.message, error);
      }
      if (data) {
        const mapped = data.map(mapInvoice);
        setInvoices(mapped);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const toFix = data.filter((row: any) => {
          const venc = new Date(row.fecha_vencimiento);
          return (row.estado === 'pendiente' || row.estado === 'parcial') && venc < today && row.saldo_pendiente > 0;
        });
        if (toFix.length > 0) {
          Promise.all(toFix.map((row: any) => supabaseAdmin.from('invoices').update({
            estado: 'vencida'
          }).eq('id', row.id))).catch(() => {});
        }
      }
    } finally {
      setIsLoading(false);
    }
  };
  const loadCreditNotes = async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('credit_notes').select('id,numero,farmacia,vendedor,vendedor_id,monto,monto_disponible,factura_aplicada,estado,fecha_creacion,notas').order('fecha_creacion', {
        ascending: false
      }).limit(300);
      if (error) {
        console.error('[CuentasCobrar] Error cargando notas de crédito:', error.message, error);
      }
      if (data) setCreditNotes(data.map(mapCreditNote));
    } catch (err) {
      console.error('[CuentasCobrar] Exception cargando NC:', err);
    }
  };
  const loadNCApplications = async () => {
    try {
      const {
        data
      } = await supabaseAdmin.from('nc_applications').select('id,farmacia,invoice_id,invoice_numero,monto,nc_monto_aplicado,estado,created_at').order('created_at', {
        ascending: false
      }).limit(200);
      if (data) setNcApplications(data);
    } catch {}
  };
  const handleAnularNCApplication = async () => {
    if (!anularNCApp) return;
    setIsAnulandoNC(true);
    try {
      const ncApp = anularNCApp;
      // 1. Restore invoice saldo_pendiente
      const restoredAmount = Number(ncApp.total_original) - Number(ncApp.total_a_pagar);
      const {
        data: currentInvoice
      } = await supabaseAdmin.from('invoices').select('id, saldo_pendiente, monto').eq('id', ncApp.invoice_id).single();
      if (currentInvoice) {
        const newBalance = Math.min(currentInvoice.monto, Number(currentInvoice.saldo_pendiente) + restoredAmount);
        const newStatus = newBalance <= 0 ? 'pagada' : newBalance < currentInvoice.monto ? 'parcial' : 'pendiente';
        await supabaseAdmin.from('invoices').update({
          saldo_pendiente: newBalance,
          estado: newStatus
        }).eq('id', ncApp.invoice_id);
      }
      // 2. Restore credit note monto_disponible
      const ncMontoAplicado = Number(ncApp.nc_monto_aplicado) || 0;
      if (ncMontoAplicado > 0) {
        const {
          data: currentNC
        } = await supabaseAdmin.from('credit_notes').select('id, monto, monto_disponible').eq('id', ncApp.credit_note_id).single();
        if (currentNC) {
          const newNCBalance = Math.min(currentNC.monto, Number(currentNC.monto_disponible) + ncMontoAplicado);
          const newNCStatus = newNCBalance >= currentNC.monto ? 'disponible' : 'parcial';
          await supabaseAdmin.from('credit_notes').update({
            monto_disponible: newNCBalance,
            estado: newNCStatus,
            factura_aplicada: null
          }).eq('id', ncApp.credit_note_id);
        }
      }
      // 3. Propagate to related visits — restore saldo in facturasDetalle
      try {
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', ncApp.farmacia).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === ncApp.invoice_id || fd.numeroFactura && fd.numeroFactura === ncApp.invoice_numero);
            if (matchIdx === -1) continue;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
              if (idx !== matchIdx) return fd;
              return {
                ...fd,
                saldoNuevo: currentInvoice ? Math.min(currentInvoice.monto, Number(currentInvoice.saldo_pendiente) + restoredAmount) : fd.saldoAnterior || fd.saldoNuevo,
                estado: 'pendiente'
              };
            });
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando anulación NC a visitas:', propagateErr);
      }
      // 4. Delete the nc_application record
      await supabaseAdmin.from('nc_applications').delete().eq('id', ncApp.id);
      // 5. Refresh data
      await loadInvoices();
      await loadCreditNotes();
      await loadNCApplications();
      addActivityLog('Anuló aplicación NC', `NC: ${ncApp.credit_note_numero} | Factura: ${ncApp.invoice_numero} | Farmacia: ${ncApp.farmacia}`);
      setAnularNCApp(null);
    } catch (err) {
      console.error('Error anulando aplicación NC:', err);
    } finally {
      setIsAnulandoNC(false);
    }
  };
  useEffect(() => {
    loadInvoices();
    loadCreditNotes();
    loadNCApplications();
  }, []);
  // Auto-refresh when invoices or credit notes change in Supabase
  const refreshAllData = useCallback(() => {
    loadInvoices();
    loadCreditNotes();
    loadNCApplications();
  }, []);
  useRealtimeRefresh(['invoices', 'credit_notes', 'nc_applications', 'app_settings'], refreshAllData);
  // Invoice filtering
  // Admin and users with cuentas_cobrar permission see all invoices
  const canSeeAllInvoices = isAdmin || user?.permisos?.cuentas_cobrar === true;
  const filteredInvoices = invoices.filter(inv => {
    if (!canSeeAllInvoices && inv.vendedorId !== user?.id) return false;
    const matchesSearch = inv.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || inv.numeroFactura.toLowerCase().includes(searchQuery.toLowerCase()) || inv.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || inv.vendedor === vendedorFilter;
    const invDate = new Date(inv.fechaEmision).toLocaleDateString('en-CA', {
      timeZone: 'America/Santo_Domingo'
    });
    const matchesDateFrom = !dateFrom || invDate >= dateFrom;
    const matchesDateTo = !dateTo || invDate <= dateTo;
    return matchesSearch && matchesStatus && matchesVendedor && matchesDateFrom && matchesDateTo;
  });
  const {
    items: sortedInvoices,
    requestSort,
    sortConfig
  } = useSortableData(filteredInvoices, {
    numeroFactura: i => i.numeroFactura,
    farmacia: i => i.farmacia,
    vendedor: i => i.vendedor,
    fechaVencimiento: i => new Date(i.fechaVencimiento).getTime(),
    subTotal: i => i.subTotal,
    itbis: i => i.itbis,
    monto: i => i.monto,
    saldoPendiente: i => i.saldoPendiente,
    estado: i => i.estado
  });
  const finalInvoices = sortConfig ? sortedInvoices : [...sortedInvoices].sort((a, b) => new Date(b.fechaEmision).getTime() - new Date(a.fechaEmision).getTime());
  // ── Global totals (unfiltered) — always show the REAL numbers ──────────────
  const allVisibleInvoices = invoices.filter(inv => {
    if (!canSeeAllInvoices && inv.vendedorId !== user?.id) return false;
    return true;
  });
  const totalPendiente = allVisibleInvoices.reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const totalVencido = allVisibleInvoices.filter(inv => inv.estado === 'vencida').reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const facturasPendientes = allVisibleInvoices.filter(inv => inv.saldoPendiente > 0).length;
  // Filtered count for table info
  const filteredCount = finalInvoices.length;
  const isFiltered = searchQuery || statusFilter !== 'all' || vendedorFilter !== 'all' || dateFrom || dateTo;
  const totalPages = Math.ceil(finalInvoices.length / ITEMS_PER_PAGE);
  const paginatedInvoices = finalInvoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Credit Notes filtering
  const filteredNC = creditNotes.filter(nc => {
    if (!canSeeAllInvoices && nc.vendedorId !== user?.id) return false;
    return nc.farmacia.toLowerCase().includes(ncSearchQuery.toLowerCase()) || nc.numero.toLowerCase().includes(ncSearchQuery.toLowerCase());
  });
  const ncTotalPages = Math.ceil(filteredNC.length / ITEMS_PER_PAGE);
  const paginatedNC = filteredNC.slice((ncPage - 1) * ITEMS_PER_PAGE, ncPage * ITEMS_PER_PAGE);
  const ncDisponibles = filteredNC.filter(nc => nc.estado === 'disponible');
  const ncAplicadas = filteredNC.filter(nc => nc.estado === 'aplicada');
  const ncMontoDisponible = ncDisponibles.reduce((sum, nc) => sum + nc.montoDisponible, 0);
  // Application tab data
  const appInvoices = invoices.filter(inv => farmaciaMatch(inv.farmacia, appFarmacia) && inv.saldoPendiente > 0 && inv.estado !== 'pagada');
  const appCreditNotes = creditNotes.filter(nc => farmaciaMatch(nc.farmacia, appFarmacia) && (nc.estado === 'disponible' || nc.estado === 'parcial') && nc.montoDisponible > 0);
  const selectedInvoice = appInvoices.find(inv => inv.id === selectedInvoiceId) || null;
  const selectedNCs = appCreditNotes.filter(nc => selectedNCIds.includes(nc.id));
  // Calculation logic (BR-03 to BR-07)
  // When multiple NCs are applied, use the CURRENT saldo to derive remaining subtotal
  const calculateApplication = () => {
    if (!selectedInvoice) return null;
    const subtotalOriginal = selectedInvoice.subTotal;
    const itbisOriginal = selectedInvoice.itbis;
    const totalOriginal = selectedInvoice.monto;
    // If saldo < monto, previous NCs have already reduced the subtotal
    // Remaining subtotal = saldoPendiente - itbis (ITBIS never changes)
    const currentSaldo = selectedInvoice.saldoPendiente;
    const remainingSubtotal = currentSaldo < totalOriginal ? Math.max(0, currentSaldo - itbisOriginal) : subtotalOriginal;
    // Sum all selected NCs, capped at remaining subtotal
    let ncMontoTotal = 0;
    const ncBreakdown: {
      id: string;
      numero: string;
      montoAplicado: number;
      montoDisponible: number;
    }[] = [];
    let subtotalLeft = remainingSubtotal;
    for (const nc of selectedNCs) {
      const aplicar = Math.min(nc.montoDisponible, Math.max(0, subtotalLeft));
      ncBreakdown.push({
        id: nc.id,
        numero: nc.numero,
        montoAplicado: aplicar,
        montoDisponible: nc.montoDisponible
      });
      ncMontoTotal += aplicar;
      subtotalLeft -= aplicar;
    }
    const subtotalAjustado = Math.max(0, remainingSubtotal - ncMontoTotal);
    // No discount applied in this section — only credit note
    const descuentoMonto = 0;
    const subtotalFinal = subtotalAjustado;
    const itbisFinal = itbisOriginal; // BR-05: ITBIS unchanged
    const totalAPagar = subtotalFinal + itbisFinal; // BR-06
    return {
      subtotalOriginal,
      itbisOriginal,
      totalOriginal,
      ncMonto: ncMontoTotal,
      ncBreakdown,
      subtotalAjustado,
      descuentoPct: 0,
      descuentoMonto,
      subtotalFinal,
      itbisFinal,
      totalAPagar,
      remainingSubtotal
    };
  };
  const calculation = calculateApplication();
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const getStatusBadge = (status: Invoice['estado']) => {
    switch (status) {
      case 'pagada':
        return <Badge variant="success" data-id="element-1834" data-mp="3knihawvk3vo0">Pagada</Badge>;
      case 'parcial':
        return <Badge variant="warning" data-id="element-1835" data-mp="3pkhejoaioiqf">Parcial</Badge>;
      case 'vencida':
        return <Badge variant="danger" data-id="element-1836" data-mp="1q9hnwris00vb">Vencida</Badge>;
      default:
        return <Badge variant="info" data-id="element-1837" data-mp="1v7rjh38ex3hg">Pendiente</Badge>;
    }
  };
  const getNCStatusBadge = (status: CreditNote['estado']) => {
    switch (status) {
      case 'disponible':
        return <Badge variant="success" data-id="element-1838" data-mp="1n96218a9b4k9">Disponible</Badge>;
      case 'aplicada':
        return <Badge variant="info" data-id="element-1839" data-mp="1j72pshfrjvxx">Aplicada</Badge>;
      case 'parcial':
        return <Badge variant="warning" data-id="element-1840" data-mp="1x6apdbfu962n">Parcial</Badge>;
      default:
        return <Badge variant="default" data-id="element-1841" data-mp="1s6fxp5o8ocgj">{status}</Badge>;
    }
  };
  // Invoice CRUD
  const isVendedor = user?.permisos?.['new-visit'] === true;
  const openAddModal = () => {
    setEditingInvoice(null);
    setFacturaError('');
    setFormData({
      ...emptyForm,
      numeroFactura: '',
      farmacia: FARMACIAS[0] || '',
      vendedor: isVendedor ? user?.nombre || '' : '',
      vendedorId: isVendedor ? user?.id || '' : ''
    });
    setIsModalOpen(true);
  };
  const openEditModal = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      numeroFactura: invoice.numeroFactura,
      farmacia: invoice.farmacia,
      vendedor: invoice.vendedor,
      vendedorId: invoice.vendedorId || '',
      fechaEmision: new Date(invoice.fechaEmision).toISOString().split('T')[0],
      fechaVencimiento: new Date(invoice.fechaVencimiento).toISOString().split('T')[0],
      subTotal: String(invoice.subTotal || 0),
      itbis: String(invoice.itbis || 0),
      monto: String(invoice.monto),
      saldoPendiente: String(invoice.saldoPendiente),
      estado: invoice.estado,
      ppPct: invoice.ppPct != null ? String(invoice.ppPct) : ''
    });
    setFacturaError('');
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!formData.numeroFactura || !formData.monto || !formData.fechaVencimiento) return;
    const isDuplicate = invoices.some(inv => inv.numeroFactura.trim().toLowerCase() === formData.numeroFactura.trim().toLowerCase() && inv.id !== editingInvoice?.id);
    if (isDuplicate) {
      setFacturaError(`El número de factura "${formData.numeroFactura}" ya existe.`);
      return;
    }
    setFacturaError('');
    setIsSaving(true);
    try {
      let finalEstado = formData.estado;
      const fechaVenc = new Date(formData.fechaVencimiento);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const saldoPend = Number(formData.saldoPendiente);
      if (finalEstado !== 'pagada' && fechaVenc < today && saldoPend > 0) finalEstado = 'vencida';
      if (finalEstado === 'vencida' && fechaVenc >= today) finalEstado = saldoPend < Number(formData.monto) && saldoPend > 0 ? 'parcial' : 'pendiente';
      const payload = {
        numero_factura: formData.numeroFactura,
        farmacia: formData.farmacia,
        vendedor: formData.vendedor,
        vendedor_id: formData.vendedorId || null,
        fecha_emision: new Date(formData.fechaEmision).toISOString(),
        fecha_vencimiento: new Date(formData.fechaVencimiento).toISOString(),
        sub_total: Number(formData.subTotal) || 0,
        itbis: Number(formData.itbis) || 0,
        monto: Number(formData.monto),
        saldo_pendiente: saldoPend,
        estado: finalEstado,
        pp_pct: formData.ppPct !== '' ? Number(formData.ppPct) : null
      };
      if (editingInvoice) {
        const {
          data
        } = await supabaseAdmin.from('invoices').update(payload).eq('id', editingInvoice.id).select().single();
        if (data) setInvoices(prev => prev.map(inv => inv.id === editingInvoice.id ? mapInvoice(data) : inv));
        addActivityLog('Editó factura', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia} | Monto: RD${formData.monto}`);
        // ── Propagate invoice changes to related visits ──────────────────────
        try {
          const {
            data: relatedVisits
          } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', editingInvoice.farmacia).not('cobro', 'is', null).limit(500);
          if (relatedVisits && relatedVisits.length > 0) {
            for (const visit of relatedVisits) {
              const cobro = visit.cobro as any;
              if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
              const hadMatch = cobro.facturasDetalle.some((fd: any) => fd.facturaId === editingInvoice.id || fd.numeroFactura && fd.numeroFactura === editingInvoice.numeroFactura);
              if (!hadMatch) continue;
              // Update the invoice details in facturasDetalle
              const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
                if (idx !== -1) return fd;
                return {
                  ...fd,
                  saldoNuevo: editingInvoice.saldoPendiente,
                  estado: editingInvoice.estado
                };
              });
              await supabaseAdmin.from('visits').update({
                cobro: {
                  ...cobro,
                  facturasDetalle: updatedDetalle
                }
              }).eq('id', visit.id);
            }
          }
        } catch (propagateErr) {
          console.error('Error propagando cambios a visitas:', propagateErr);
        }
      } else {
        const {
          data
        } = await supabaseAdmin.from('invoices').insert(payload).select().single();
        if (data) setInvoices(prev => [mapInvoice(data), ...prev]);
        addActivityLog('Creó factura', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia} | Monto: RD${formData.monto}`);
        // ── AUTO-CREATE COMMISSION RECORD for new invoice ──────────────────
        if (data && formData.vendedorId) {
          try {
            const {
              data: commConfig
            } = await supabaseAdmin.from('vendor_commissions').select('comision_venta_pct,comision_cobro_pct').eq('vendor_id', formData.vendedorId).maybeSingle();
            const pctVenta = commConfig?.comision_venta_pct || 0;
            if (pctVenta > 0) {
              const montoBase = Number(formData.monto);
              const comisionVenta = Math.round(montoBase * (pctVenta / 100) * 100) / 100;
              const matchedClient = clients.find(c => farmaciaMatch(c.nombre, formData.farmacia));
              await supabaseAdmin.from('commission_records').insert({
                vendor_id: formData.vendedorId,
                vendor_nombre: formData.vendedor,
                cliente_id: matchedClient?.id || null,
                cliente_nombre: formData.farmacia,
                tipo: 'venta',
                monto_base: montoBase,
                porcentaje: pctVenta,
                monto_comision: comisionVenta,
                referencia_id: data.id,
                fecha: new Date().toISOString()
              });
            }
          } catch (commErr) {
            console.error('Error registrando comisión de factura CxC:', commErr);
          }
        }
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingInvoice) return;
    addActivityLog('Eliminó factura', `Factura: ${editingInvoice.numeroFactura} | Farmacia: ${editingInvoice.farmacia}`);
    await supabaseAdmin.from('invoices').delete().eq('id', editingInvoice.id);
    setInvoices(prev => prev.filter(inv => inv.id !== editingInvoice.id));
    // ── Propagate deletion to related visits ─────────────────────────────
    try {
      const {
        data: relatedVisits
      } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', editingInvoice.farmacia).not('cobro', 'is', null).limit(500);
      if (relatedVisits && relatedVisits.length > 0) {
        for (const visit of relatedVisits) {
          const cobro = visit.cobro as any;
          if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
          const hadMatch = cobro.facturasDetalle.some((fd: any) => fd.facturaId === editingInvoice.id || fd.numeroFactura && fd.numeroFactura === editingInvoice.numeroFactura);
          if (!hadMatch) continue;
          // Remove the deleted invoice from facturasDetalle
          const updatedDetalle = cobro.facturasDetalle.filter((fd: any) => fd.facturaId !== editingInvoice.id && fd.numeroFactura !== editingInvoice.numeroFactura);
          // Remove from cobro.numeroFactura string
          let updatedNumeroFactura = cobro.numeroFactura || '';
          if (updatedNumeroFactura.includes(editingInvoice.numeroFactura)) {
            updatedNumeroFactura = updatedNumeroFactura.split(',').map((s: string) => s.trim()).filter((s: string) => s !== editingInvoice.numeroFactura).join(', ');
          }
          await supabaseAdmin.from('visits').update({
            cobro: {
              ...cobro,
              facturasDetalle: updatedDetalle,
              numeroFactura: updatedNumeroFactura
            }
          }).eq('id', visit.id);
        }
      }
    } catch (err) {
      console.error('Error propagando eliminación a visitas:', err);
    }
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  // Credit Note CRUD
  const openAddNCModal = () => {
    setEditingNC(null);
    setNCError('');
    const existing = creditNotes.map(nc => {
      const match = nc.numero.match(/^NC-(\d+)$/i);
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    const nextNC = `NC-${String(nextNum).padStart(5, '0')}`;
    setNCFormData({
      ...emptyNCForm,
      numero: nextNC,
      farmacia: FARMACIAS[0] || '',
      vendedor: isVendedor ? user?.nombre || '' : '',
      vendedorId: isVendedor ? user?.id || '' : ''
    });
    setIsNCModalOpen(true);
  };
  const openEditNCModal = (nc: CreditNote) => {
    setEditingNC(nc);
    setNCFormData({
      numero: nc.numero,
      farmacia: nc.farmacia,
      vendedor: nc.vendedor,
      vendedorId: nc.vendedorId || '',
      monto: String(nc.monto),
      notas: nc.notas || ''
    });
    setNCError('');
    setIsNCModalOpen(true);
  };
  const handleSaveNC = async () => {
    if (!ncFormData.numero || !ncFormData.monto || !ncFormData.farmacia) return;
    const isDuplicate = creditNotes.some(nc => nc.numero.trim().toLowerCase() === ncFormData.numero.trim().toLowerCase() && nc.id !== editingNC?.id);
    if (isDuplicate) {
      setNCError(`El número de NC "${ncFormData.numero}" ya existe.`);
      return;
    }
    setNCError('');
    setIsSaving(true);
    try {
      const monto = Number(ncFormData.monto) || 0;
      const payload = {
        numero: ncFormData.numero,
        farmacia: ncFormData.farmacia,
        vendedor: ncFormData.vendedor,
        vendedor_id: ncFormData.vendedorId || null,
        monto,
        monto_disponible: editingNC ? editingNC.montoDisponible : monto,
        estado: 'disponible',
        notas: ncFormData.notas
      };
      if (editingNC) {
        const {
          data
        } = await supabaseAdmin.from('credit_notes').update(payload).eq('id', editingNC.id).select().single();
        if (data) setCreditNotes(prev => prev.map(nc => nc.id === editingNC.id ? mapCreditNote(data) : nc));
      } else {
        const {
          data
        } = await supabaseAdmin.from('credit_notes').insert(payload).select().single();
        if (data) setCreditNotes(prev => [mapCreditNote(data), ...prev]);
      }
      addActivityLog(editingNC ? 'Editó nota de crédito' : 'Creó nota de crédito', `NC: ${ncFormData.numero} | Farmacia: ${ncFormData.farmacia} | Monto: RD${ncFormData.monto}`);
      setIsNCModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteNC = async () => {
    if (!editingNC) return;
    addActivityLog('Eliminó nota de crédito', `NC: ${editingNC.numero} | Farmacia: ${editingNC.farmacia}`);
    await supabaseAdmin.from('credit_notes').delete().eq('id', editingNC.id);
    setCreditNotes(prev => prev.filter(nc => nc.id !== editingNC.id));
    setIsNCDeleteModalOpen(false);
    setIsNCModalOpen(false);
  };
  // Apply NC to Invoice
  const handleApplyNC = async () => {
    if (!selectedInvoice || !calculation) return;
    setIsApplying(true);
    try {
      // Record application for each selected NC (only those with monto > 0)
      for (const ncItem of calculation.ncBreakdown) {
        if (ncItem.montoAplicado <= 0) continue;
        const nc = selectedNCs.find(n => n.id === ncItem.id);
        if (!nc) continue;
        const {
          error: ncAppError
        } = await supabaseAdmin.from('nc_applications').insert({
          credit_note_id: nc.id,
          credit_note_numero: nc.numero,
          invoice_id: selectedInvoice.id,
          invoice_numero: selectedInvoice.numeroFactura,
          farmacia: selectedInvoice.farmacia,
          subtotal_original: calculation.subtotalOriginal,
          itbis_original: calculation.itbisOriginal,
          total_original: calculation.totalOriginal,
          nc_monto_aplicado: ncItem.montoAplicado,
          subtotal_ajustado: calculation.subtotalAjustado,
          descuento_pct: calculation.descuentoPct,
          descuento_monto: calculation.descuentoMonto,
          subtotal_final: calculation.subtotalFinal,
          itbis_final: calculation.itbisFinal,
          total_a_pagar: calculation.totalAPagar,
          aplicado_por: user?.nombre || 'Sistema'
        });
        if (ncAppError) {
          console.error('Error guardando nc_applications:', ncAppError.message);
        }
      }
      // Update invoice balance — totalAPagar is already based on current saldo
      const newBalance = Math.max(0, calculation.totalAPagar);
      const newStatus = newBalance <= 0 ? 'pagada' : newBalance < selectedInvoice.monto ? 'parcial' : 'pendiente';
      await supabaseAdmin.from('invoices').update({
        saldo_pendiente: newBalance,
        estado: newStatus
      }).eq('id', selectedInvoice.id);
      // Update each credit note used
      for (const ncItem of calculation.ncBreakdown) {
        const nc = selectedNCs.find(n => n.id === ncItem.id);
        if (!nc || ncItem.montoAplicado <= 0) continue;
        const newNCBalance = nc.montoDisponible - ncItem.montoAplicado;
        const newNCStatus = newNCBalance <= 0 ? 'aplicada' : 'parcial';
        await supabaseAdmin.from('credit_notes').update({
          monto_disponible: Math.max(0, newNCBalance),
          estado: newNCStatus,
          factura_aplicada: selectedInvoice.numeroFactura
        }).eq('id', nc.id);
      }
      // ── Propagate NC application changes to related visits ─────────────
      try {
        const newSaldo = newBalance;
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', selectedInvoice.farmacia).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === selectedInvoice.id || fd.numeroFactura && fd.numeroFactura === selectedInvoice.numeroFactura);
            if (matchIdx === -1) continue;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
              if (idx !== matchIdx) return fd;
              return {
                ...fd,
                saldoNuevo: newSaldo,
                estado: newStatus
              };
            });
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando NC a visitas:', propagateErr);
      }
      await loadInvoices();
      await loadCreditNotes();
      const ncNames = calculation.ncBreakdown.map(n => n.numero).join(', ') || 'N/A';
      addActivityLog('Aplicó nota(s) de crédito', `NC: ${ncNames} → Factura: ${selectedInvoice.numeroFactura} | Farmacia: ${selectedInvoice.farmacia} | Monto total NC: RD${calculation.ncMonto.toFixed(2)}`);
      setApplicationSuccess(true);
      setSelectedInvoiceId(null);
      setSelectedNCIds([]);
    } finally {
      setIsApplying(false);
    }
  };
  // Payment modal
  const openPaymentModal = () => {
    setPaymentClient('');
    setPaymentAmounts({});
    setPaymentMetodo('efectivo');
    setPaymentDone(false);
    setLastPaymentData(null);
    setIsPaymentModalOpen(true);
  };
  const clientInvoices = invoices.filter(inv => farmaciaMatch(inv.farmacia, paymentClient) && inv.saldoPendiente > 0 && inv.estado !== 'pagada');
  const handlePaymentAmountChange = (invoiceId: string, amount: string) => {
    const val = parseFloat(amount);
    if (isNaN(val) || val < 0) {
      const next = {
        ...paymentAmounts
      };
      delete next[invoiceId];
      setPaymentAmounts(next);
      return;
    }
    setPaymentAmounts(prev => ({
      ...prev,
      [invoiceId]: val
    }));
  };
  const totalPayment = Object.values(paymentAmounts).reduce((a, b) => a + b, 0);
  const handleProcessPayment = async () => {
    if (totalPayment <= 0) return;
    setIsSaving(true);
    try {
      const updates = Object.entries(paymentAmounts).map(async ([invoiceId, amount]) => {
        const invoice = invoices.find(i => i.id === invoiceId);
        if (!invoice) return;
        const newBalance = Math.max(0, invoice.saldoPendiente - amount);
        const newStatus = newBalance <= 0 ? 'pagada' : newBalance < invoice.monto ? 'parcial' : 'pendiente';
        const {
          data
        } = await supabaseAdmin.from('invoices').update({
          saldo_pendiente: newBalance,
          estado: newStatus
        }).eq('id', invoiceId).select().single();
        return data ? mapInvoice(data) : null;
      });
      const results = await Promise.all(updates);
      const updatedInvoices = results.filter(Boolean) as Invoice[];
      setInvoices(prev => prev.map(inv => {
        const u = updatedInvoices.find(u => u.id === inv.id);
        return u || inv;
      }));
      const paidInvoices = Object.entries(paymentAmounts).filter(([, amt]) => amt > 0).map(([invoiceId, abono]) => {
        const inv = invoices.find(i => i.id === invoiceId);
        return {
          numeroFactura: inv?.numeroFactura || '',
          monto: inv?.monto || 0,
          abono
        };
      });
      // ── Propagate payment changes to related visits ────────────────────
      try {
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', paymentClient).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            let changed = false;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any) => {
              // Find if this factura was part of the payment
              const matchedUpdate = updatedInvoices.find(u => u.id === fd.facturaId || fd.numeroFactura && u.numeroFactura === fd.numeroFactura);
              if (!matchedUpdate) return fd;
              changed = true;
              return {
                ...fd,
                saldoNuevo: matchedUpdate.saldoPendiente,
                estado: matchedUpdate.estado
              };
            });
            if (!changed) continue;
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando pago a visitas:', propagateErr);
      }
      setLastPaymentData({
        farmacia: paymentClient,
        invoices: paidInvoices,
        total: totalPayment
      });
      addActivityLog('Registró pago múltiple', `Farmacia: ${paymentClient} | Total: RD${totalPayment.toFixed(2)} | Facturas: ${paidInvoices.map(i => i.numeroFactura).join(', ')}`);
      setPaymentDone(true);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDownloadRecibo = async () => {
    if (!lastPaymentData) return;
    setIsDownloadingRecibo(true);
    try {
      await downloadReciboPago(lastPaymentData.farmacia, lastPaymentData.invoices, lastPaymentData.total, paymentMetodo);
    } finally {
      setIsDownloadingRecibo(false);
    }
  };
  const handleDownloadPDF = async () => {
    if (!lastPaymentData) return;
    setIsDownloadingRecibo(true);
    try {
      await downloadReciboPago(lastPaymentData.farmacia, lastPaymentData.invoices, lastPaymentData.total, paymentMetodo);
    } finally {
      setIsDownloadingRecibo(false);
    }
  };
  const generateEstadoCuenta = async () => {
    if (!estadoCuentaClient) return;
    setIsGeneratingEstadoCuenta(true);
    try {
      // 1. Filter invoices for the selected client and date range
      const clientInvs = invoices.filter(inv => {
        if (!farmaciaMatch(inv.farmacia, estadoCuentaClient)) return false;
        const invDate = new Date(inv.fechaEmision).toLocaleDateString('en-CA', {
          timeZone: 'America/Santo_Domingo'
        });
        if (dateFrom && invDate < dateFrom) return false;
        if (dateTo && invDate > dateTo) return false;
        return true;
      }).sort((a, b) => new Date(b.fechaEmision).getTime() - new Date(a.fechaEmision).getTime());
      // 2. Create PDF
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const ml = 15;
      const mr = 15;
      // 3. Header band (dark navy)
      const genDate = new Date().toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      const headerH = 36;
      doc.setFillColor(13, 27, 70);
      doc.rect(0, 0, pw, headerH, 'F');
      // Logo white rounded box
      const logoBase64 = await getLogoBase64();
      if (logoBase64) {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(ml, 5, 26, 26, 3, 3, 'F');
        doc.addImage(logoBase64, 'PNG', ml + 1, 6, 24, 24);
      }
      // Company name
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Miladys Rodriguez & Asoc.', ml + 30, 16);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(123, 163, 216);
      doc.text('Sistema de Control de Vendedores', ml + 30, 23);
      // Date top right
      doc.setFontSize(8);
      doc.setTextColor(123, 163, 216);
      doc.text('Generado: ' + genDate, pw - mr, 16, {
        align: 'right'
      });
      // Accent bar
      doc.setFillColor(41, 82, 196);
      doc.rect(0, headerH, pw, 2, 'F');
      // Title section
      let y = headerH + 2;
      const hasDateRange = dateFrom || dateTo;
      const titleStripH = hasDateRange ? 30 : 24;
      doc.setFillColor(248, 250, 255);
      doc.rect(0, y, pw, titleStripH, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(0, y + titleStripH, pw, y + titleStripH);
      y += 6;
      doc.setTextColor(13, 27, 70);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('ESTADO DE CUENTA', ml, y + 5);
      // Records pill (top right)
      const pillW = 30;
      doc.setFillColor(41, 82, 196);
      doc.roundedRect(pw - mr - pillW, y, pillW, 8, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.text(clientInvs.length + ' facturas', pw - mr - pillW / 2, y + 5.5, {
        align: 'center'
      });
      // Date range box
      if (hasDateRange) {
        const boxY = y + 10;
        const boxH = 10;
        const boxW = pw - ml - mr;
        doc.setFillColor(234, 240, 255);
        doc.setDrawColor(41, 82, 196);
        doc.setLineWidth(0.4);
        doc.roundedRect(ml, boxY, boxW, boxH, 2, 2, 'FD');
        doc.setFillColor(41, 82, 196);
        doc.roundedRect(ml + 2, boxY + 2, 6, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text('F', ml + 5, boxY + 6.2, {
          align: 'center'
        });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(41, 82, 196);
        const periodLabel = 'Período:  ';
        doc.text(periodLabel, ml + 10, boxY + 6.5);
        const periodLabelW = doc.getTextWidth(periodLabel);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(13, 27, 70);
        const periodText = `${dateFrom ? formatDate(new Date(dateFrom + 'T00:00:00')) : 'Inicio'}  al  ${dateTo ? formatDate(new Date(dateTo + 'T00:00:00')) : 'Hoy'}`;
        doc.text(periodText, ml + 10 + periodLabelW, boxY + 6.5);
      }
      y = headerH + 2 + titleStripH + 2;
      // Client info row
      doc.setFillColor(255, 255, 255);
      doc.rect(ml, y, pw - ml - mr, 10, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.rect(ml, y, pw - ml - mr, 10, 'S');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(41, 82, 196);
      doc.text('Cliente:', ml + 3, y + 6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(13, 27, 70);
      doc.text(estadoCuentaClient, ml + 18, y + 6.5);
      y += 14;
      // Table
      const cols = ['Factura', 'Emisión', 'Vencimiento', 'Monto', 'Saldo Pendiente', 'Estado'];
      const colW = [30, 25, 25, 35, 35, 30];
      const tableW = colW.reduce((a, b) => a + b, 0);
      doc.setFillColor(13, 27, 70);
      doc.rect(ml, y, tableW, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      let x = ml;
      cols.forEach((col, i) => {
        doc.text(col, x + colW[i] / 2, y + 5.5, {
          align: 'center'
        });
        x += colW[i];
      });
      y += 8;
      let totalMonto = 0;
      let totalSaldo = 0;
      clientInvs.forEach((inv, i) => {
        if (y > ph - 30) {
          doc.addPage();
          y = 15;
          // Draw header again
          doc.setFillColor(13, 27, 70);
          doc.rect(ml, y, tableW, 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          let hx = ml;
          cols.forEach((col, idx) => {
            doc.text(col, hx + colW[idx] / 2, y + 5.5, {
              align: 'center'
            });
            hx += colW[idx];
          });
          y += 8;
        }
        totalMonto += inv.monto;
        totalSaldo += inv.saldoPendiente;
        const isEven = i % 2 === 0;
        doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
        doc.rect(ml, y, tableW, 7, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.rect(ml, y, tableW, 7, 'S');
        doc.setTextColor(51, 65, 85);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        let cx = ml;
        // Factura
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text(inv.numeroFactura, cx + colW[0] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[0];
        // Emision
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);
        doc.text(formatDate(inv.fechaEmision), cx + colW[1] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[1];
        // Vencimiento
        doc.text(formatDate(inv.fechaVencimiento), cx + colW[2] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[2];
        // Monto
        doc.text(formatCurrency(inv.monto), cx + colW[3] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[3];
        // Saldo
        doc.setFont('helvetica', 'bold');
        doc.text(formatCurrency(inv.saldoPendiente), cx + colW[4] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[4];
        // Estado
        const estadoStr = inv.estado.charAt(0).toUpperCase() + inv.estado.slice(1);
        if (inv.estado === 'pagada') doc.setTextColor(5, 150, 105);else if (inv.estado === 'vencida') doc.setTextColor(220, 38, 38);else if (inv.estado === 'parcial') doc.setTextColor(217, 119, 6);else doc.setTextColor(30, 41, 59);
        doc.text(estadoStr, cx + colW[5] / 2, y + 4.5, {
          align: 'center'
        });
        y += 7;
      });
      // Summary row
      if (y > ph - 40) {
        doc.addPage();
        y = 15;
      }
      y += 2;
      doc.setFillColor(248, 250, 252);
      doc.rect(ml, y, tableW, 8, 'F');
      doc.setDrawColor(180, 200, 220);
      doc.setLineWidth(0.3);
      doc.rect(ml, y, tableW, 8, 'S');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(`Total Facturas: ${clientInvs.length}`, ml + 5, y + 5.5);
      let sumX = ml + colW[0] + colW[1] + colW[2];
      doc.text(formatCurrency(totalMonto), sumX + colW[3] / 2, y + 5.5, {
        align: 'center'
      });
      sumX += colW[3];
      doc.text(formatCurrency(totalSaldo), sumX + colW[4] / 2, y + 5.5, {
        align: 'center'
      });
      // Footer
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(13, 27, 70);
        doc.rect(0, ph - 9, pw, 9, 'F');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(123, 163, 216);
        doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', pw / 2, ph - 3.5, {
          align: 'center'
        });
        doc.text(`Página ${i} de ${totalPages}`, pw - mr, ph - 3.5, {
          align: 'right'
        });
      }
      doc.save(`estado-cuenta-${estadoCuentaClient.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
      addActivityLog('Generó estado de cuenta', `Cliente: ${estadoCuentaClient} | Facturas: ${clientInvs.length}`);
      setShowEstadoCuenta(false);
    } catch (err) {
      console.error('Error generating estado de cuenta:', err);
      alert('Error al generar el estado de cuenta.');
    } finally {
      setIsGeneratingEstadoCuenta(false);
    }
  };
  return <div className="space-y-6" data-id="element-1842" data-mp="2pi9n7ulloafo">
      <PageHeader title="Cuentas por Cobrar" subtitle="Gestión de facturas, notas de crédito y cobros" icon={<DollarSignIcon className="w-6 h-6" data-id="element-1844" data-mp="2ubm4fjny9eye" />} data-id="element-1843" data-mp="2ph891h9af7tq">
        
        <div className="flex flex-wrap gap-2" data-id="element-1845" data-mp="ithd8q7p5k4i">
          {canSeeDiscount && <Button variant="secondary" leftIcon={<SettingsIcon className="w-4 h-4" data-id="element-1847" data-mp="dtd4xngxzvpd" />} onClick={() => {
          setDiscountConfigDraft({
            pct: discountPct,
            days: discountDays
          });
          setIsDiscountConfigOpen(true);
        }} data-id="element-1846" data-mp="dvze1rjhej9p">
            
              Config. Descuento
            </Button>}
          <Button variant="secondary" leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1849" data-mp="3r4c5evl48brv" />} onClick={openPaymentModal} data-id="element-1848" data-mp="3r2rb972pquuj">
            
            Registrar Pago
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1851" data-mp="4tga143ym0lv" />} onClick={openAddModal} data-id="element-1850" data-mp="4tq1kw4nkfto">
            
            Nueva Factura
          </Button>
        </div>
      </PageHeader>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl overflow-x-auto no-scrollbar" data-id="element-1852" data-mp="1g9n45n5eyy9d">
        <button onClick={() => setActiveTab('facturas')} className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'facturas' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1853" data-mp="1uy2gd02iwgnl">
          
          <FileTextIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-1854" data-mp="2dmz92zi9v13p" />
          Facturas
        </button>
        <button onClick={() => setActiveTab('notas-credito')} className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'notas-credito' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1855" data-mp="28nw8psd8lj22">
          
          <FileMinusIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-1856" data-mp="2wf1knwg23r73" />
          Notas de Crédito
        </button>
        <button onClick={() => {
        setActiveTab('aplicar-nc');
        setApplicationSuccess(false);
      }} className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'aplicar-nc' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1857" data-mp="2rl71cu4d5e8j">
          
          <ArrowRightLeftIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-1858" data-mp="s1sneo4a6u6t" />
          Aplicar NC
        </button>
        <button onClick={() => setActiveTab('historial-nc')} className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'historial-nc' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1859" data-mp="x15j7yd4rblj">
          
          <ClockIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-1860" data-mp="1fuiwmpksuzde" />
          Historial NC
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: FACTURAS */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'facturas' && <>
          {canSeeDiscount && <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl" data-id="element-1861" data-mp="3qls75aavjt6q">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1862" data-mp="3ltqzk1oniqn1">
                <PercentIcon className="w-5 h-5 text-emerald-600" data-id="element-1863" data-mp="3fht3n714by2" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-1864" data-mp="3vjk087n99lci">
                <p className="text-sm font-semibold text-slate-900" data-id="element-1865" data-mp="3vmxzh70mhefq">
                  Política de Descuento por Pronto Pago
                </p>
                <p className="text-xs text-slate-600 mt-0.5" data-id="element-1866" data-mp="8gxc79pnc8dt">
                  <strong className="text-emerald-700" data-id="element-1867" data-mp="2f6bymf9e67j8">
                    {discountPct}% de descuento
                  </strong>{' '}
                  para cobros dentro de los primeros{' '}
                  <strong className="text-emerald-700" data-id="element-1868" data-mp="pp800op1ugeq">
                    {discountDays} días
                  </strong>{' '}
                  desde la emisión.
                </p>
              </div>
            </div>}

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1869" data-mp="fzy5712tmjmx">
            <StatsCard title="Total por Cobrar" value={formatCurrency(totalPendiente)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1871" data-mp="6y74kan512zx" />} variant="primary" data-id="element-1870" data-mp="kyhunbgmidk1" />
          

            <StatsCard title="Saldo Vencido" value={formatCurrency(totalVencido)} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-1873" data-mp="3t6q7uw4tnnf5" />} variant="danger" data-id="element-1872" data-mp="bycn91bfjf5v" />
          

            <StatsCard title="Facturas Pendientes" value={facturasPendientes} icon={<ClockIcon className="w-5 h-5" data-id="element-1875" data-mp="1rebsa3pr2v1w" />} variant="warning" data-id="element-1874" data-mp="1z3ziq75qwry" />
          
          </div>

          {isFiltered && <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700" data-id="element-1876" data-mp="1wd4wf5j5d9pe">
              <FilterIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1877" data-mp="3of7vmoarce6y" />
              <span data-id="element-1878" data-mp="3ofpnz9uau89l">
                Mostrando <strong data-id="element-1879" data-mp="3jg4p0zf4pzwv">{filteredCount}</strong> de{' '}
                <strong data-id="element-1880" data-mp="3jetjov61ck3n">{allVisibleInvoices.length}</strong> facturas (filtro
                activo). Los totales de arriba reflejan <strong data-id="element-1881" data-mp="3egs4ilbqd4xg">todas</strong>{' '}
                las facturas.
              </span>
            </div>}

          <div className="flex flex-col gap-3" data-id="element-1882" data-mp="39jryubism46v">
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-1883" data-mp="34q5st274wyk1">
              <div className="relative flex-1" data-id="element-1884" data-mp="30pn1rad2fwiw">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1885" data-mp="30qet28zmr83d" />
                <input type="text" placeholder="Buscar por farmacia, factura o vendedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-1886" data-mp="2vs3mq88jnfpw" />
              
              </div>
              <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1887" data-mp="2lmlvf9a4za3u">
              
                <option value="all" data-id="element-1888" data-mp="1xwrjqv1jzrl6">Todos los vendedores</option>
                {VENDEDORES.map(v => <option key={v} value={v} data-id="element-1889" data-mp="22wdxd5lan9ui">
                    {v}
                  </option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1890" data-mp="27nvvllvxmwbs">
              
                <option value="all" data-id="element-1891" data-mp="174iywlaonxqf">Todos los estados</option>
                <option value="pendiente" data-id="element-1892" data-mp="1270lahlq0uc3">Pendiente</option>
                <option value="parcial" data-id="element-1893" data-mp="126h6h9rdam2a">Pago Parcial</option>
                <option value="vencida" data-id="element-1894" data-mp="1gvwwgnj2qngx">Vencida</option>
                <option value="pagada" data-id="element-1895" data-mp="1gxhn4229pm9i">Pagada</option>
              </select>
              <Button variant="secondary" className="ml-auto" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1897" data-mp="o781z7cixge5" />} onClick={() => setShowEstadoCuenta(true)} data-id="element-1896" data-mp="1bxc2v6ybv21g">
              
                Estado de Cuenta
              </Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-id="element-1898" data-mp="y6grno0cw8up">
              <span className="text-xs text-slate-500 font-medium" data-id="element-1899" data-mp="t5szgz0e5lyw">Desde</span>
              <input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }} className="px-2.5 py-1.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1900" data-mp="29k2fn5uhc4en" />
            

              <span className="text-xs text-slate-500 font-medium" data-id="element-1901" data-mp="234byflvwxe4w">Hasta</span>
              <input type="date" value={dateTo} onChange={e => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }} className="px-2.5 py-1.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1902" data-mp="236eykyqs4nnc" />
            

              {(dateFrom || dateTo) && <button onClick={() => {
            setDateFrom('');
            setDateTo('');
            setCurrentPage(1);
          }} className="text-xs text-rose-600 hover:text-rose-700 font-medium px-2 py-1 rounded-lg hover:bg-rose-50 transition-colors" data-id="element-1903" data-mp="2q4029rkk1zfj">
              
                  Limpiar fechas
                </button>}
            </div>
          </div>

          <Card padding="none" data-id="element-1904" data-mp="2p2ey45g5abw5">
            {filteredInvoices.length === 0 ? <div className="p-8 sm:p-16 text-center" data-id="element-1905" data-mp="2k3u08cod84av">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1906" data-mp="2k2962o5yqndj">
                  <FileTextIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-1907" data-mp="3csr4hbvqs0km" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-1908" data-mp="37tejyxscf5l7">
                  No hay facturas
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1909" data-mp="32ta2b4fp84te">
                  No se encontraron facturas con los filtros aplicados
                </p>
              </div> : <>
                {/* ── MOBILE CARD VIEW (< sm) ── */}
                <div className="sm:hidden divide-y divide-slate-100" data-id="element-1910" data-mp="1mgl6xvi4hqyb">
                  {paginatedInvoices.map(invoice => <div key={invoice.id} className={`p-3.5 ${invoice.estado === 'vencida' ? 'bg-rose-50/30' : ''}`} data-id="element-1911" data-mp="1z2kjh1yo4oh">
                
                      <div className="flex items-start justify-between gap-2" data-id="element-1912" data-mp="6vatudth93nq">
                        <div className="min-w-0 flex-1" data-id="element-1913" data-mp="bvff0h5bymka">
                          <p className="font-bold text-slate-900 text-sm" data-id="element-1914" data-mp="gnqbtb4rxs8m">
                            {invoice.numeroFactura}
                          </p>
                          <p className="text-xs text-slate-700 mt-0.5 truncate" data-id="element-1915" data-mp="low6vflwprlb">
                            {invoice.farmacia}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5" data-id="element-1916" data-mp="uoja86y757h0">
                            <UserIcon className="w-3 h-3 text-slate-400" data-id="element-1917" data-mp="zmuhc9x3x3vj" />
                            <span className="text-[11px] text-slate-500" data-id="element-1918" data-mp="2wekznyxcwj64">
                              {invoice.vendedor}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-1919" data-mp="31fipmxolyhi6">
                            Vence: {formatDate(invoice.fechaVencimiento)}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right" data-id="element-1920" data-mp="q815bx25lbh2">
                          <p className="font-bold text-slate-900 text-sm" data-id="element-1921" data-mp="q8t2hcbefh9g">
                            {formatCurrency(invoice.saldoPendiente)}
                          </p>
                          <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-1922" data-mp="3unaob7801hbg">
                            de {formatCurrency(invoice.monto)}
                          </p>
                          <div className="mt-1" data-id="element-1923" data-mp="7htbk5kcfxdj">
                            {getStatusBadge(invoice.estado)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-slate-100" data-id="element-1924" data-mp="1iwymfwump288">
                        <button onClick={() => openEditModal(invoice)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1925" data-mp="1nv07uofj1dmv">
                    
                          <EditIcon className="w-4 h-4" data-id="element-1926" data-mp="33f96drny6waz" />
                        </button>
                        <button onClick={() => {
                  setEditingInvoice(invoice);
                  setIsDeleteModalOpen(true);
                }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1927" data-mp="2ymps0shfrx54">
                    
                          <TrashIcon className="w-4 h-4" data-id="element-1928" data-mp="2kqm3nft8uj0k" />
                        </button>
                      </div>
                    </div>)}
                </div>

                {/* ── DESKTOP TABLE (sm+) ── */}
                <div className="hidden sm:block overflow-x-auto" data-id="element-1929" data-mp="dybkuqdau38t">
                  <table className="w-full" data-id="element-1930" data-mp="dvflqyppt8is">
                    <thead data-id="element-1931" data-mp="8ygtklpm3bn8">
                      <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1932" data-mp="8z8d2y5xpix5">
                        <SortableTableHeader label="Factura" sortKey="numeroFactura" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1933" data-mp="23dvxqat7ur45" />
                    

                        <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1934" data-mp="1ygdn8fzjw3u1" />
                    

                        <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1935" data-mp="2d4gx40gprzo4" />
                    

                        <SortableTableHeader label="Vencimiento" sortKey="fechaVencimiento" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1936" data-mp="15p2e6g7ozgzj" />
                    

                        <SortableTableHeader label="Sub Total" sortKey="subTotal" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1937" data-mp="136qtliepbaky" />
                    

                        <SortableTableHeader label="ITBIS" sortKey="itbis" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1938" data-mp="p7r7ahenv7ry" />
                    

                        <SortableTableHeader label="Monto" sortKey="monto" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1939" data-mp="3nv549ui1rmm0" />
                    

                        <SortableTableHeader label="Saldo" sortKey="saldoPendiente" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1940" data-mp="34x99voj8soiy" />
                    

                        <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1941" data-mp="3iuo3l5gj3igq" />
                    

                        <th className="px-5 py-4 w-20" data-id="element-1942" data-mp="1lyl0ein8dhuk"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-1943" data-mp="1h6jqv4hg6588">
                      {paginatedInvoices.map(invoice => <tr key={invoice.id} className={`hover:bg-slate-50/50 transition-colors ${invoice.estado === 'vencida' ? 'bg-rose-50/30' : ''}`} data-id="element-1944" data-mp="1c91knlute43x">
                    
                          <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1945" data-mp="24v2zjl4esk0u">
                            {invoice.numeroFactura}
                          </td>
                          <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1946" data-mp="1uxy9393j62e8">
                            {invoice.farmacia}
                          </td>
                          <td className="px-5 py-4" data-id="element-1947" data-mp="jjuh28gi6obt">
                            <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-1948" data-mp="jle1mb96zlje">
                              <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1949" data-mp="fgo4kj2ss0ww" />
                              {invoice.vendedor}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1950" data-mp="lx6hreyviqxg">
                            {formatDate(invoice.fechaVencimiento)}
                          </td>
                          <td className="px-5 py-4 text-right text-slate-600 text-sm" data-id="element-1951" data-mp="bzs8q0fa54f6">
                            {formatCurrency(invoice.subTotal)}
                          </td>
                          <td className="px-5 py-4 text-right text-slate-600 text-sm" data-id="element-1952" data-mp="2zdcw74ow6xf">
                            {formatCurrency(invoice.itbis)}
                          </td>
                          <td className="px-5 py-4 text-right text-slate-700" data-id="element-1953" data-mp="3kead9s60i1u1">
                            {formatCurrency(invoice.monto)}
                          </td>
                          <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-1954" data-mp="3bfyqk189no6v">
                            {formatCurrency(invoice.saldoPendiente)}
                          </td>
                          <td className="px-5 py-4 text-center" data-id="element-1955" data-mp="2wtj82xh307u1">
                            {getStatusBadge(invoice.estado)}
                          </td>
                          <td className="px-5 py-4" data-id="element-1956" data-mp="35tekt6qacaw6">
                            <div className="flex items-center gap-1" data-id="element-1957" data-mp="35uzhp302o5x7">
                              <button onClick={() => openEditModal(invoice)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1958" data-mp="30u20asmvzfwr">
                          
                                <EditIcon className="w-4 h-4" data-id="element-1959" data-mp="3aqokrgbaw4y9" />
                              </button>
                              <button onClick={() => {
                        setEditingInvoice(invoice);
                        setIsDeleteModalOpen(true);
                      }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1960" data-mp="3olqnxvorjg7d">
                          
                                <TrashIcon className="w-4 h-4" data-id="element-1961" data-mp="14psizmp9kpvr" />
                              </button>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </>}
          </Card>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredInvoices.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1962" data-mp="l22y7zfqvpw1" />
        
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: NOTAS DE CRÉDITO */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'notas-credito' && <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1963" data-mp="7aelbtxo8xe0">
            <StatsCard title="NC Disponibles" value={ncDisponibles.length} icon={<FileMinusIcon className="w-5 h-5" data-id="element-1965" data-mp="3vugnql1yzqdq" />} variant="success" data-id="element-1964" data-mp="78ldibbbo74f" />
          

            <StatsCard title="NC Aplicadas" value={ncAplicadas.length} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-1967" data-mp="3m0gdk4rtzuy5" />} variant="primary" data-id="element-1966" data-mp="3qvdjh2ttdo6t" />
          

            <StatsCard title="Monto Disponible" value={formatCurrency(ncMontoDisponible)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1969" data-mp="386yt8hrbme72" />} variant="warning" data-id="element-1968" data-mp="3h1d66dodpcn0" />
          
          </div>

          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1970" data-mp="337m6ry4d3950">
            <div className="relative flex-1" data-id="element-1971" data-mp="3385cmggqm72o">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1972" data-mp="2y9awxne2gyms" />
              <input type="text" placeholder="Buscar por farmacia o número de NC..." value={ncSearchQuery} onChange={e => setNCSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-1973" data-mp="2ta7pxxej0ic6" />
            
            </div>
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1975" data-mp="y8kxtmxrkd4u" />} onClick={openAddNCModal} data-id="element-1974" data-mp="y8cna1p45jwt">
            
              Nueva NC
            </Button>
          </div>

          <Card padding="none" data-id="element-1976" data-mp="6hevczukm3ce">
            {filteredNC.length === 0 ? <div className="p-8 sm:p-16 text-center" data-id="element-1977" data-mp="1j3lwq86tuup">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1978" data-mp="1kog2eqlbbs1">
                  <FileMinusIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-1979" data-mp="fccrsgwxfy8h" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-1980" data-mp="ahyto6qvrd04">
                  No hay notas de crédito
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1981" data-mp="3ex8b3ch2vase">
                  Crea una nueva nota de crédito para comenzar
                </p>
              </div> : <>
                {/* ── MOBILE CARD VIEW (< sm) ── */}
                <div className="sm:hidden divide-y divide-slate-100" data-id="element-1982" data-mp="39mw4j90c6dsu">
                  {paginatedNC.map(nc => <div key={nc.id} className="p-3.5" data-id="element-1983" data-mp="2uu2c6mzyu36r">
                      <div className="flex items-start justify-between gap-2" data-id="element-1984" data-mp="2zvrqcbihiwz2">
                        <div className="min-w-0 flex-1" data-id="element-1985" data-mp="3nkay8pxmh7qm">
                          <p className="font-bold text-slate-900 text-sm" data-id="element-1986" data-mp="3niq3p0bb5osr">
                            {nc.numero}
                          </p>
                          <p className="text-xs text-slate-700 mt-0.5 truncate" data-id="element-1987" data-mp="3dn68yh8hcw5l">
                            {nc.farmacia}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5" data-id="element-1988" data-mp="3inas6f0rw4zi">
                            <UserIcon className="w-3 h-3 text-slate-400" data-id="element-1989" data-mp="225ogidwcniyd" />
                            <span className="text-[11px] text-slate-500" data-id="element-1990" data-mp="277dpzp3xbokc">
                              {nc.vendedor}
                            </span>
                          </div>
                          {nc.facturaAplicada && <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-1991" data-mp="8palkgpo8ai2">
                              Aplicada a: {nc.facturaAplicada}
                            </p>}
                        </div>
                        <div className="flex-shrink-0 text-right" data-id="element-1992" data-mp="3v293gi8rtwo1">
                          <p className="font-bold text-slate-900 text-sm" data-id="element-1993" data-mp="3q6k2cah3s7pm">
                            {formatCurrency(nc.montoDisponible)}
                          </p>
                          <p className="text-[11px] text-rose-500 mt-0.5" data-id="element-1994" data-mp="3h3ix0c02akn0">
                            -{formatCurrency(nc.monto)}
                          </p>
                          <div className="mt-1" data-id="element-1995" data-mp="3c7ke33xi5utz">
                            {getNCStatusBadge(nc.estado)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-slate-100" data-id="element-1996" data-mp="1ac1mwt2mz4g1">
                        <button onClick={() => openEditNCModal(nc)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1997" data-mp="1mwgpel8pssf5">
                    
                          <EditIcon className="w-4 h-4" data-id="element-1998" data-mp="1wmjhmj80i2cf" />
                        </button>
                        <button onClick={() => {
                  setEditingNC(nc);
                  setIsNCDeleteModalOpen(true);
                }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1999" data-mp="yz1cdidk7id6">
                    
                          <TrashIcon className="w-4 h-4" data-id="element-2000" data-mp="1dwjui1vqexbn" />
                        </button>
                      </div>
                    </div>)}
                </div>

                {/* ── DESKTOP TABLE (sm+) ── */}
                <div className="hidden sm:block overflow-x-auto" data-id="element-2001" data-mp="2pd1sw7iw3a0x">
                  <table className="w-full" data-id="element-2002" data-mp="2mx222q5xsxv6">
                    <thead data-id="element-2003" data-mp="2iuovy0na2xxv">
                      <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2004" data-mp="2ivgrxcksexoo">
                        <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2005" data-mp="2wu6qkwn995f5">
                          Número
                        </th>
                        <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2006" data-mp="2rxgc2dr3mhvu">
                          Cód. Cliente
                        </th>
                        <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2007" data-mp="31t1iqp7446bx">
                          Farmacia
                        </th>
                        <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2008" data-mp="3allad5ozlan9">
                          Vendedor
                        </th>
                        <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2009" data-mp="3kk13tfbhkvzx">
                          Monto Original
                        </th>
                        <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2010" data-mp="7yc2485v2ywj">
                          Disponible
                        </th>
                        <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2011" data-mp="3u74ywr7su8m7">
                          Factura Aplicada
                        </th>
                        <th className="px-5 py-4 text-center text-xs font-semibold text-slate-500 uppercase" data-id="element-2012" data-mp="3kgsfx3fmuni6">
                          Estado
                        </th>
                        <th className="px-5 py-4 w-20" data-id="element-2013" data-mp="3bhf6garidyxg"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-2014" data-mp="3gfgoqth41u7v">
                      {paginatedNC.map(nc => <tr key={nc.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-2015" data-mp="31k0ou7tun43k">
                    
                          <td className="px-5 py-4 font-medium text-slate-900" data-id="element-2016" data-mp="36jwu0aea91kp">
                            {nc.numero}
                          </td>
                          <td className="px-5 py-4" data-id="element-2017" data-mp="2ilped0cvjeyd">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-semibold bg-slate-100 text-slate-700" data-id="element-2018" data-mp="2dnnnvud4cdcv">
                              {clients.find(c => c.nombre === nc.farmacia)?.codigo || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-slate-700" data-id="element-2019" data-mp="31e9udh7h5pkr">
                            {nc.farmacia}
                          </td>
                          <td className="px-5 py-4" data-id="element-2020" data-mp="2wg8aiu285m88">
                            <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-2021" data-mp="2re0tvr6g9hzk">
                              <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2022" data-mp="1kz8js2vizfgb" />
                              {nc.vendedor}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right text-rose-600 font-medium" data-id="element-2023" data-mp="3rnr0nat37ihk">
                            -{formatCurrency(nc.monto)}
                          </td>
                          <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-2024" data-mp="5g0z1bvi8xdm">
                            {formatCurrency(nc.montoDisponible)}
                          </td>
                          <td className="px-5 py-4 text-slate-600" data-id="element-2025" data-mp="a8kao3amtifs">
                            {nc.facturaAplicada || '—'}
                          </td>
                          <td className="px-5 py-4 text-center" data-id="element-2026" data-mp="j6e7dfcb6e3h">
                            {getNCStatusBadge(nc.estado)}
                          </td>
                          <td className="px-5 py-4" data-id="element-2027" data-mp="t5lh7sju3qlu">
                            <div className="flex items-center gap-1" data-id="element-2028" data-mp="t4agk1loqyyy">
                              <button onClick={() => openEditNCModal(nc)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-2029" data-mp="2pyf0kib4eoq2">
                          
                                <EditIcon className="w-4 h-4" data-id="element-2030" data-mp="10n1j649wl11u" />
                              </button>
                              <button onClick={() => {
                        setEditingNC(nc);
                        setIsNCDeleteModalOpen(true);
                      }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2031" data-mp="vnynvbjq6972">
                          
                                <TrashIcon className="w-4 h-4" data-id="element-2032" data-mp="csgwtaocu81q" />
                              </button>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </>}
          </Card>
          <Pagination currentPage={ncPage} totalPages={ncTotalPages} onPageChange={setNCPage} totalItems={filteredNC.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2033" data-mp="235pkuxvdwi3f" />
        
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: APLICAR NC */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'aplicar-nc' && <>
          {applicationSuccess ? <Card className="text-center py-12" data-id="element-2034" data-mp="2oeit3lucotwi">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2035" data-mp="2oftw3jfz1xmk">
                <CheckCircleIcon className="w-10 h-10 text-emerald-600" data-id="element-2036" data-mp="2td3qzem4r1hs" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2037" data-mp="2flpapfjlxeef">
                ¡Aplicación Exitosa!
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-2038" data-mp="2jlpr35q9nzo4">
                La nota de crédito y descuento han sido aplicados correctamente.
              </p>
              <Button onClick={() => {
          setApplicationSuccess(false);
          setAppFarmacia('');
        }} data-id="element-2039" data-mp="3c4d8aa256ujx">
            
                Nueva Aplicación
              </Button>
            </Card> : <>
              {/* Available NCs Summary — only shown after selecting a farmacia */}
              {appFarmacia && (() => {
          const farmaciaAvailableNCs = creditNotes.filter(nc => (nc.estado === 'disponible' || nc.estado === 'parcial') && nc.montoDisponible > 0 && farmaciaMatch(nc.farmacia, appFarmacia));
          if (farmaciaAvailableNCs.length === 0) return null;
          const totalDisponible = farmaciaAvailableNCs.reduce((sum, nc) => sum + nc.montoDisponible, 0);
          return <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-2040" data-mp="2hazc95dihwtr">
                      <div className="flex items-center gap-2 mb-3" data-id="element-2041" data-mp="2d94c447bhphk">
                        <FileMinusIcon className="w-5 h-5 text-amber-600" data-id="element-2042" data-mp="2dayy7lubrnn8" />
                        <h3 className="font-semibold text-amber-900" data-id="element-2043" data-mp="sq2038jm0fta">
                          Notas de Crédito Disponibles para {appFarmacia} (
                          {farmaciaAvailableNCs.length})
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-2044" data-mp="16qcswh4hc4h3">
                        {farmaciaAvailableNCs.map(nc => <div key={nc.id} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white text-amber-800 border border-amber-200" data-id="element-2045" data-mp="93m41jv1zbnj">
                    
                            <span className="font-semibold" data-id="element-2046" data-mp="izpedd026glk">{nc.numero}</span>
                            <span className="text-xs text-amber-600" data-id="element-2047" data-mp="nsil26f37z0a">
                              {formatCurrency(nc.montoDisponible)}
                            </span>
                          </div>)}
                      </div>
                      <p className="text-xs text-amber-700 font-semibold mt-2" data-id="element-2048" data-mp="3jg4hzs2q6q2l">
                        Total disponible: {formatCurrency(totalDisponible)}
                      </p>
                    </div>;
        })()}

              {/* Step 1: Select Farmacia */}
              <Card data-id="element-2049" data-mp="3t55vnblssvde">
                <div className="flex items-center gap-3 mb-4" data-id="element-2050" data-mp="3t7s3lcclpcw5">
                  <div className="w-8 h-8 bg-[#2952C4] text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-2051" data-mp="3o85sb8gc26pz">
                    1
                  </div>
                  <h3 className="font-semibold text-slate-900" data-id="element-2052" data-mp="2wggfpqtqecu2">
                    Seleccionar Farmacia
                  </h3>
                </div>
                <SearchableSelect label="" value={appFarmacia} onChange={val => {
            setAppFarmacia(val);
            setSelectedInvoiceId(null);
            setSelectedNCIds([]);
          }} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2053" data-mp="3add2nh6qba5o" />
            
              </Card>

              {appFarmacia && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2054" data-mp="22q3nov0woq16">
                  {/* Step 2: Select Invoice */}
                  <Card data-id="element-2055" data-mp="1tnc94mcqhe5b">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2056" data-mp="1to46a1lzbjxp">
                      <div className="w-8 h-8 bg-[#2952C4] text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-2057" data-mp="1ylvv2mrbupxo">
                        2
                      </div>
                      <h3 className="font-semibold text-slate-900" data-id="element-2058" data-mp="2qdbr17erxp0k">
                        Seleccionar Factura
                      </h3>
                    </div>
                    {appInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl" data-id="element-2059" data-mp="2hkrp9y36r4bi">
                        <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-2060" data-mp="2hiopimc8dwtl" />
                        <p className="text-slate-600 font-medium" data-id="element-2061" data-mp="358j2r50gpned">
                          No hay facturas pendientes
                        </p>
                      </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2062" data-mp="1lgascguga2ti">
                        <table className="w-full text-sm" data-id="element-2063" data-mp="1gfdg0kw4g2zx">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2064" data-mp="1cejolhjh062a">
                            <tr data-id="element-2065" data-mp="1cgo0alnm29d4">
                              <th className="px-3 py-2 w-10" data-id="element-2066" data-mp="17gi5wxpyi8rc"></th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-500" data-id="element-2067" data-mp="17i2y4gostfm1">
                                Nº Factura
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-2068" data-mp="20579r4iecs6s">
                                Subtotal
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-2069" data-mp="1qdjfywydaz25">
                                ITBIS
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-2070" data-mp="2j3ji4o1hhy1b">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-2071" data-mp="2vealgldlix1u">
                            {appInvoices.map(inv => <tr key={inv.id} className={`cursor-pointer transition-colors ${selectedInvoiceId === inv.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`} onClick={() => setSelectedInvoiceId(inv.id)} data-id="element-2072" data-mp="30barwxecy1th">
                      
                                <td className="px-3 py-3 text-center" data-id="element-2073" data-mp="3eaiu7z5thq1w">
                                  <input type="radio" name="invoice" checked={selectedInvoiceId === inv.id} onChange={() => setSelectedInvoiceId(inv.id)} className="w-4 h-4 text-[#2952C4]" data-id="element-2074" data-mp="3j44wqyi9odjz" />
                        
                                </td>
                                <td className="px-3 py-3 font-medium text-slate-900" data-id="element-2075" data-mp="1wyium8otollq">
                                  {inv.numeroFactura}
                                </td>
                                <td className="px-3 py-3 text-right text-slate-700" data-id="element-2076" data-mp="26rznnah4xwoc">
                                  {formatCurrency(inv.subTotal)}
                                </td>
                                <td className="px-3 py-3 text-right text-slate-600" data-id="element-2077" data-mp="2gnjehigue568">
                                  {formatCurrency(inv.itbis)}
                                </td>
                                <td className="px-3 py-3 text-right font-bold text-slate-900" data-id="element-2078" data-mp="2ppj1qsiga5hm">
                                  {formatCurrency(inv.monto)}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>}
                  </Card>

                  {/* Step 3: Select NC (optional) */}
                  <Card data-id="element-2079" data-mp="3201ux5ptqdan">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2080" data-mp="321uw45ho45b9">
                      <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-2081" data-mp="36y3h3zijqsqc">
                        3
                      </div>
                      <h3 className="font-semibold text-slate-900" data-id="element-2082" data-mp="2x7h48d27sgbc">
                        Seleccionar Nota de Crédito{' '}
                        <span className="text-slate-400 font-normal" data-id="element-2083" data-mp="2jb3ob8dbwmyz">
                          (opcional)
                        </span>
                      </h3>
                    </div>
                    {appCreditNotes.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl" data-id="element-2084" data-mp="2e9o5zo2jcuka">
                        <FileMinusIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-2085" data-mp="2ebr0akaq19ut" />
                        <p className="text-slate-500 font-medium" data-id="element-2086" data-mp="8ev8cdrmd6gt">
                          No hay NC disponibles para esta farmacia
                        </p>
                      </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2087" data-mp="2wdtvrjsxwoom">
                        <table className="w-full text-sm" data-id="element-2088" data-mp="2rg0sayj0u8s1">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2089" data-mp="2moagsp8clez1">
                            <tr data-id="element-2090" data-mp="2hpz2a173ygah">
                              <th className="px-3 py-2 w-10" data-id="element-2091" data-mp="2honxpz5u94ib"></th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-500" data-id="element-2092" data-mp="2dnuevk796fwa">
                                Nº NC
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-2093" data-mp="28r3yevrjdiac">
                                Disponible
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-2094" data-mp="1z341pgzn1m6">
                            {appCreditNotes.map(nc => {
                    const isSelected = selectedNCIds.includes(nc.id);
                    const toggleNC = () => setSelectedNCIds(prev => isSelected ? prev.filter(id => id !== nc.id) : [...prev, nc.id]);
                    return <tr key={nc.id} className={`cursor-pointer transition-colors ${isSelected ? 'bg-amber-50' : 'hover:bg-slate-50'}`} onClick={toggleNC} data-id="element-2095" data-mp="j7vv4l1h0o4i">
                          
                                  <td className="px-3 py-3 text-center" data-id="element-2096" data-mp="3mqox00kvzoiu">
                                    <input type="checkbox" checked={isSelected} onChange={toggleNC} className="w-4 h-4 text-amber-500 rounded" data-id="element-2097" data-mp="3rmxfnnyalzur" />
                            
                                  </td>
                                  <td className="px-3 py-3 font-medium text-slate-900" data-id="element-2098" data-mp="1bydzrqhuexk1">
                                    {nc.numero}
                                  </td>
                                  <td className="px-3 py-3 text-right font-bold text-rose-600" data-id="element-2099" data-mp="3hdjtv8avixbe">
                                    -{formatCurrency(nc.montoDisponible)}
                                  </td>
                                </tr>;
                  })}
                          </tbody>
                        </table>
                      </div>}
                  </Card>
                </div>}

              {/* Calculation Breakdown */}
              {selectedInvoice && calculation && <Card className="border-2 border-[#2952C4]/20 bg-gradient-to-br from-blue-50/50 to-white" data-id="element-2100" data-mp="2kpvo7ps00x8q">
                  <div className="flex items-center gap-3 mb-6" data-id="element-2101" data-mp="2poqdnakh1k1t">
                    <div className="w-10 h-10 bg-[#2952C4] text-white rounded-xl flex items-center justify-center" data-id="element-2102" data-mp="iwz297wjwa9u">
                      <CalculatorIcon className="w-5 h-5" data-id="element-2103" data-mp="ntg1ax5ag91p" />
                    </div>
                    <div data-id="element-2104" data-mp="xzz1btx8op2b">
                      <h3 className="font-bold text-slate-900" data-id="element-2105" data-mp="xx33s6pb02ee">
                        Resumen de Cálculo
                      </h3>
                      <p className="text-sm text-slate-500" data-id="element-2106" data-mp="16sapf94vs0f4">
                        Factura {selectedInvoice.numeroFactura}{' '}
                        {selectedNCs.length > 0 ? `+ ${selectedNCs.map(nc => nc.numero).join(', ')}` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-2107" data-mp="1bsyhzz8rcpbg">
                    <table className="w-full" data-id="element-2108" data-mp="1ppu1ymqjs16a">
                      <tbody className="divide-y divide-slate-100" data-id="element-2109" data-mp="1loieonv5bs19">
                        <tr className="bg-slate-50" data-id="element-2110" data-mp="1ln7aimxsgi9m">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-2111" data-mp="3rirq459dq5kw">
                            Subtotal original
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-2112" data-mp="1qfsc2y9j1lvm">
                            {formatCurrency(calculation.subtotalOriginal)}
                          </td>
                        </tr>
                        {calculation.ncBreakdown.map(ncItem => <tr key={ncItem.id} className={ncItem.montoAplicado > 0 ? 'bg-rose-50' : 'bg-slate-50'} data-id="element-2113" data-mp="20gb1s9nrvwlg">
                    
                            <td className="px-5 py-3 text-sm" data-id="element-2114" data-mp="2ecesvu3cnotp">
                              <span className={ncItem.montoAplicado > 0 ? 'text-rose-700' : 'text-slate-400'} data-id="element-2115" data-mp="2j4pud1dqniod">
                        
                                NC {ncItem.numero}
                              </span>
                              {ncItem.montoAplicado === 0 && <span className="ml-2 text-xs text-slate-400" data-id="element-2116" data-mp="lgm0cx8sh2sq">
                                  (sin saldo restante)
                                </span>}
                            </td>
                            <td className={`px-5 py-3 text-right font-bold ${ncItem.montoAplicado > 0 ? 'text-rose-600' : 'text-slate-400'}`} data-id="element-2117" data-mp="ch8l1nxzhk03">
                      
                              {ncItem.montoAplicado > 0 ? `(${formatCurrency(ncItem.montoAplicado)})` : '$0.00'}
                            </td>
                          </tr>)}
                        <tr data-id="element-2118" data-mp="vd74lgczi9vi">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-2119" data-mp="qfykvbc6txs5">
                            Subtotal ajustado
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-2120" data-mp="1nw4be75zt9wh">
                            {formatCurrency(calculation.subtotalAjustado)}
                          </td>
                        </tr>
                        {calculation.descuentoMonto > 0 && <tr className="bg-emerald-50" data-id="element-2121" data-mp="17og85di60r2w">
                            <td className="px-5 py-3 text-sm text-emerald-700" data-id="element-2122" data-mp="17lv72qpoyf67">
                              Descuento {calculation.descuentoPct}%
                            </td>
                            <td className="px-5 py-3 text-right font-bold text-emerald-600" data-id="element-2123" data-mp="ynrvam4icwrl">
                              ({formatCurrency(calculation.descuentoMonto)})
                            </td>
                          </tr>}
                        {calculation.descuentoMonto > 0 && <tr data-id="element-2124" data-mp="1cl6q66dj5wqy">
                            <td className="px-5 py-3 text-sm text-slate-600" data-id="element-2125" data-mp="1hm47yhumoorx">
                              Subtotal final
                            </td>
                            <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-2126" data-mp="25andipmamngb">
                              {formatCurrency(calculation.subtotalFinal)}
                            </td>
                          </tr>}
                        <tr className="bg-slate-50" data-id="element-2127" data-mp="3pu92soophxca">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-2128" data-mp="3ptqwez8by2qn">
                            ITBIS (fijo)
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-700" data-id="element-2129" data-mp="3dwen3ttnhtl">
                            {formatCurrency(calculation.itbisFinal)}
                          </td>
                        </tr>
                        <tr className="bg-[#2952C4]" data-id="element-2130" data-mp="36w6ign73sokt">
                          <td className="px-5 py-4 text-sm font-bold text-white" data-id="element-2131" data-mp="36xhh68loz652">
                            TOTAL A PAGAR
                          </td>
                          <td className="px-5 py-4 text-right text-xl font-bold text-white" data-id="element-2132" data-mp="3fv1onza5dyo4">
                            {formatCurrency(calculation.totalAPagar)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 flex justify-end" data-id="element-2133" data-mp="3owsi8q1je901">
                    <Button onClick={handleApplyNC} isLoading={isApplying} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-2135" data-mp="1smbluusu8we" />} data-id="element-2134" data-mp="3twevgzhd7p8u">
                
                      Aplicar y Guardar
                    </Button>
                  </div>
                </Card>}
            </>}
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: HISTORIAL NC */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'historial-nc' && (() => {
      // Filter NC applications by vendor — non-admins only see their own
      const filteredNcApps = isAdmin ? ncApplications : ncApplications.filter(ncApp => {
        // Match by invoice vendor
        const invoice = invoices.find(inv => inv.id === ncApp.invoice_id);
        if (invoice && invoice.vendedorId === user?.id) return true;
        // Fallback: match by credit note vendor
        const creditNote = creditNotes.find(nc => nc.id === ncApp.credit_note_id);
        if (creditNote && creditNote.vendedorId === user?.id) return true;
        // Fallback: match by aplicado_por name
        if (ncApp.aplicado_por === user?.nombre) return true;
        return false;
      });
      const ncAppTotalPages = Math.ceil(filteredNcApps.length / ITEMS_PER_PAGE);
      const paginatedNCApps = filteredNcApps.slice((ncAppPage - 1) * ITEMS_PER_PAGE, ncAppPage * ITEMS_PER_PAGE);
      return <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2136" data-mp="1i91t2hugbjjs">
                <StatsCard title="Total Aplicaciones" value={filteredNcApps.length} icon={<ArrowRightLeftIcon className="w-5 h-5" data-id="element-2138" data-mp="316wp8w0abmaq" />} variant="primary" data-id="element-2137" data-mp="1ibe7il103bs3" />
              

                <StatsCard title="Monto Total Aplicado" value={formatCurrency(filteredNcApps.reduce((sum, nc) => sum + Number(nc.nc_monto_aplicado || 0), 0))} icon={<DollarSignIcon className="w-5 h-5" data-id="element-2140" data-mp="2igoscvn6hs2z" />} variant="warning" data-id="element-2139" data-mp="366j57d7hzgn8" />
              

                <StatsCard title="Descuento Total" value={formatCurrency(filteredNcApps.reduce((sum, nc) => sum + Number(nc.descuento_monto || 0), 0))} icon={<PercentIcon className="w-5 h-5" data-id="element-2142" data-mp="1zhkqc6u20ykt" />} variant="success" data-id="element-2141" data-mp="2n7nck6bafabi" />
              
              </div>

              <Card padding="none" data-id="element-2143" data-mp="24gxa2ipmppj6">
                {filteredNcApps.length === 0 ? <div className="p-16 text-center" data-id="element-2144" data-mp="1kulvs2zwho1v">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-2145" data-mp="1kv43pwvxdqpm">
                      <ArrowRightLeftIcon className="w-8 h-8 text-slate-400" data-id="element-2146" data-mp="1fy3vpgfimdvv" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-2147" data-mp="1tt7f40d7573p">
                      No hay aplicaciones
                    </h3>
                    <p className="text-slate-500 text-sm" data-id="element-2148" data-mp="23soi2a12d6vz">
                      No se han aplicado notas de crédito a facturas aún
                    </p>
                  </div> : <>
                    {/* Mobile cards */}
                    <div className="md:hidden divide-y divide-slate-100" data-id="element-2149" data-mp="2dlm0yh9ywxv4">
                      {paginatedNCApps.map(ncApp => <div key={ncApp.id} className="p-4" data-id="element-2150" data-mp="2mjy2vfgabi2v">
                          <div className="flex items-start justify-between gap-2" data-id="element-2151" data-mp="2mkh93ywkoi12">
                            <div className="flex-1 min-w-0" data-id="element-2152" data-mp="2hmpjjbjeh801">
                              <p className="font-semibold text-slate-900 text-sm" data-id="element-2153" data-mp="2u3bhr45tlpkh">
                                NC: {ncApp.credit_note_numero}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2154" data-mp="3420oq1w6hply">
                                → Factura: {ncApp.invoice_numero}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5" data-id="element-2155" data-mp="2z0uvm2ylw0bw">
                                {ncApp.farmacia}
                              </p>
                            </div>
                            <button onClick={() => setAnularNCApp(ncApp)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0" title="Anular aplicación" data-id="element-2156" data-mp="26g3cgm8e6gig">
                        
                              <UndoIcon className="w-4 h-4" data-id="element-2157" data-mp="1skspdv0e2zon" />
                            </button>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-id="element-2158" data-mp="35zzix00717z">
                            <div className="bg-rose-50 rounded-lg p-2" data-id="element-2159" data-mp="7wgfoov7l0y1">
                              <p className="text-rose-500 mb-0.5" data-id="element-2160" data-mp="7xrkms0e4eqq">
                                NC Aplicada
                              </p>
                              <p className="font-bold text-rose-700" data-id="element-2161" data-mp="3ubjcs4hnr8jl">
                                -
                                {formatCurrency(Number(ncApp.nc_monto_aplicado || 0))}
                              </p>
                            </div>
                            <div className="bg-emerald-50 rounded-lg p-2" data-id="element-2162" data-mp="cwciiks66mc0">
                              <p className="text-emerald-500 mb-0.5" data-id="element-2163" data-mp="hue1z6ticno0">
                                Desc. {Number(ncApp.descuento_pct || 0)}%
                              </p>
                              <p className="font-bold text-emerald-700" data-id="element-2164" data-mp="2xo4xwbrnjjno">
                                -
                                {formatCurrency(Number(ncApp.descuento_monto || 0))}
                              </p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-2" data-id="element-2165" data-mp="yetvdj3vgfb2">
                              <p className="text-slate-400 mb-0.5" data-id="element-2166" data-mp="ycqx6bskffv9">
                                Total Original
                              </p>
                              <p className="font-bold text-slate-700" data-id="element-2167" data-mp="pef8ifb9es5g">
                                {formatCurrency(Number(ncApp.total_original || 0))}
                              </p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-2" data-id="element-2168" data-mp="bennhumbsbhy">
                              <p className="text-blue-500 mb-0.5" data-id="element-2169" data-mp="bggyfm3z1hvr">
                                Total a Pagar
                              </p>
                              <p className="font-bold text-blue-700" data-id="element-2170" data-mp="25s5drpe47mgo">
                                {formatCurrency(Number(ncApp.total_a_pagar || 0))}
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-2" data-id="element-2171" data-mp="1bicz5hxwa7d">
                            {new Date(ncApp.fecha_aplicacion || ncApp.created_at).toLocaleDateString('es-DO', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                            {' · '}Por: {ncApp.aplicado_por}
                          </p>
                        </div>)}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto" data-id="element-2172" data-mp="t2508jmva9ph">
                      <table className="w-full" data-id="element-2173" data-mp="t3zi1p2udlpc">
                        <thead data-id="element-2174" data-mp="xzokq1a5ripv">
                          <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2175" data-mp="y2t0lvwzk2wd">
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2176" data-mp="wk2oajjj89c5">
                              Fecha
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2177" data-mp="15l0lahvjzgl8">
                              NC
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2178" data-mp="1fhvld57h5p1m">
                              Factura
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2179" data-mp="1p7ymjsor29b1">
                              Farmacia
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2180" data-mp="1kbhoo6rr0gkr">
                              Total Original
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2181" data-mp="1t7yyn6odm8ex">
                              NC Aplicada
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2182" data-mp="2htwwedkj0mdh">
                              Descuento
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2183" data-mp="27uxoix7l4urj">
                              Total a Pagar
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2184" data-mp="1ywm091u6y929">
                              Aplicado Por
                            </th>
                            <th className="px-5 py-4 w-16" data-id="element-2185" data-mp="1p72g7suqs95t"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-2186" data-mp="1b71fqeie33qv">
                          {paginatedNCApps.map(ncApp => <tr key={ncApp.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-2187" data-mp="1g75pllosl084">
                        
                              <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2188" data-mp="3d2h0p7npbl8r">
                                {new Date(ncApp.fecha_aplicacion || ncApp.created_at).toLocaleDateString('es-DO', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                              </td>
                              <td className="px-5 py-4" data-id="element-2189" data-mp="3rs6go90gdtt3">
                                <span className="font-semibold text-slate-900 text-sm" data-id="element-2190" data-mp="3rusnu7jfm7as">
                                  {ncApp.credit_note_numero}
                                </span>
                              </td>
                              <td className="px-5 py-4" data-id="element-2191" data-mp="2beg0w4zz1h3m">
                                <span className="font-medium text-slate-700 text-sm" data-id="element-2192" data-mp="2bcv5kd5u1u4p">
                                  {ncApp.invoice_numero}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2193" data-mp="vte12gbd214r">
                                {ncApp.farmacia}
                              </td>
                              <td className="px-5 py-4 text-right text-sm text-slate-700" data-id="element-2194" data-mp="14ou0dcuvx8hq">
                                {formatCurrency(Number(ncApp.total_original || 0))}
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-2195" data-mp="1jlcf38272all">
                                <span className="text-sm font-semibold text-rose-600" data-id="element-2196" data-mp="1jiykf0bff0ek">
                                  -
                                  {formatCurrency(Number(ncApp.nc_monto_aplicado || 0))}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-2197" data-mp="36h99y68y6xf1">
                                <span className="text-sm font-semibold text-emerald-600" data-id="element-2198" data-mp="36fg1clery34e">
                                  -
                                  {formatCurrency(Number(ncApp.descuento_monto || 0))}
                                </span>
                                {Number(ncApp.descuento_pct) > 0 && <span className="text-xs text-slate-400 ml-1" data-id="element-2199" data-mp="2nj6p83nfnmn1">
                                    ({ncApp.descuento_pct}%)
                                  </span>}
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-2200" data-mp="2rkrv351jv0nq">
                                <span className="text-sm font-bold text-[#2952C4]" data-id="element-2201" data-mp="2rmmjwufxzcx3">
                                  {formatCurrency(Number(ncApp.total_a_pagar || 0))}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-sm text-slate-500" data-id="element-2202" data-mp="fdo0rt4ienjb">
                                {ncApp.aplicado_por}
                              </td>
                              <td className="px-5 py-4" data-id="element-2203" data-mp="5ef6t09n98wy">
                                <button onClick={() => setAnularNCApp(ncApp)} title="Anular aplicación de NC" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2204" data-mp="abyqifp42dwa">
                            
                                  <UndoIcon className="w-4 h-4" data-id="element-2205" data-mp="oayd7ht2cipt" />
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </>}
              </Card>
              {filteredNcApps.length > ITEMS_PER_PAGE && <Pagination currentPage={ncAppPage} totalPages={ncAppTotalPages} onPageChange={setNcAppPage} totalItems={filteredNcApps.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2206" data-mp="2ns64gtg7x1iy" />}
            </>;
    })()}

      {/* Anular NC Application Modal */}
      <Modal isOpen={!!anularNCApp} onClose={() => setAnularNCApp(null)} title="Anular Aplicación de Nota de Crédito" size="sm" data-id="element-2207" data-mp="32ezcrerbl71n">
        
        <ModalBody data-id="element-2208" data-mp="29qto4gx5axwj">
          <div className="text-center space-y-3" data-id="element-2209" data-mp="29seia5fjsetv">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-2210" data-mp="2eqpugmtbf1f9">
              <UndoIcon className="w-7 h-7 text-rose-500" data-id="element-2211" data-mp="2is1lmwru7upk" />
            </div>
            <p className="text-slate-600" data-id="element-2212" data-mp="2iq729mw7sanl">
              ¿Estás seguro de anular la aplicación de la nota de crédito{' '}
              <strong data-id="element-2213" data-mp="3u76p42zslxa8">{anularNCApp?.credit_note_numero}</strong> a la factura{' '}
              <strong data-id="element-2214" data-mp="3u9b5vlisiqrx">{anularNCApp?.invoice_numero}</strong>?
            </p>
            <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1" data-id="element-2215" data-mp="1ijbm6cj6or7g">
              <p className="text-slate-500" data-id="element-2216" data-mp="1ejks9zyd1uyp">
                Farmacia:{' '}
                <strong className="text-slate-700" data-id="element-2217" data-mp="19r9miggxvey8">
                  {anularNCApp?.farmacia}
                </strong>
              </p>
              <p className="text-slate-500" data-id="element-2218" data-mp="14s6tjue8gz6m">
                NC aplicada:{' '}
                <strong className="text-rose-600" data-id="element-2219" data-mp="zuohhv4x63ue">
                  -{formatCurrency(Number(anularNCApp?.nc_monto_aplicado || 0))}
                </strong>
              </p>
              <p className="text-slate-500" data-id="element-2220" data-mp="qsf9pd11cokp">
                Descuento:{' '}
                <strong className="text-emerald-600" data-id="element-2221" data-mp="quryoa57nz79">
                  -{formatCurrency(Number(anularNCApp?.descuento_monto || 0))}
                </strong>
              </p>
            </div>
            <p className="text-xs text-slate-400" data-id="element-2222" data-mp="2l4ly5dixx1uy">
              Se restaurará el saldo de la factura y el monto disponible de la
              nota de crédito.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2223" data-mp="3436z09ovh06d">
          <Button variant="secondary" onClick={() => setAnularNCApp(null)} data-id="element-2224" data-mp="343qdfgfbd6fn">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularNCApplication} isLoading={isAnulandoNC} leftIcon={<UndoIcon className="w-4 h-4" data-id="element-2226" data-mp="2v8sgzzcyjbjk" />} data-id="element-2225" data-mp="2qcv2lyl98f9f">
            
            Anular Aplicación
          </Button>
        </ModalFooter>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* MODALS */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      {/* Invoice Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingInvoice ? 'Editar Factura' : 'Nueva Factura'} size="lg" data-id="element-2227" data-mp="3dztfyne2v734">
        
        <ModalBody data-id="element-2228" data-mp="22n98hpjqoq8a">
          <div className="space-y-4" data-id="element-2229" data-mp="22nixpawymtcx">
            {facturaError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-2230" data-mp="1v1ngri1kcf5n">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-2231" data-mp="1q33wdm2mbbfe" />
                {facturaError}
              </div>}
            {!editingInvoice && <button type="button" onClick={() => setIsCotizacionModalOpen(true)} className="group w-full relative overflow-hidden rounded-2xl border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 text-left transition-all duration-300 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100/50 hover:scale-[1.01] active:scale-[0.99]" data-id="element-2232" data-mp="1z41w3s60wws6">
              
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-indigo-100/60 to-transparent rounded-bl-full -translate-y-4 translate-x-4 group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-500" data-id="element-2233" data-mp="1gchl241xp72i" />
                <div className="relative flex items-center gap-4" data-id="element-2234" data-mp="11cdmkm16zb8i">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md shadow-indigo-200/60 group-hover:shadow-lg group-hover:shadow-indigo-300/60 transition-shadow duration-300" data-id="element-2235" data-mp="11a0ydr4uc4n0">
                    <SearchIcon className="w-5 h-5 text-white" data-id="element-2236" data-mp="1g2b9yzrarwwn" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2237" data-mp="1k1k07un4pcng">
                    <p className="font-bold text-slate-900 text-sm group-hover:text-indigo-900 transition-colors" data-id="element-2238" data-mp="1p16d22z1oqvq">
                      Buscar Cotización (Pedido)
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 group-hover:text-indigo-400 transition-colors" data-id="element-2239" data-mp="1tyol7r58n22o">
                      Importar datos desde un pedido existente
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors" data-id="element-2240" data-mp="4l31o3dj5wn7">
                    <svg className="w-4 h-4 text-indigo-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-2241" data-mp="2hxz3259f6dk8">
                    
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-2242" data-mp="30v8n9omkjv2j" />
                    
                    </svg>
                  </div>
                </div>
              </button>}
            <div className="grid grid-cols-2 gap-4" data-id="element-2243" data-mp="2muxwell9eghd">
              <Input label="Número de Factura" value={formData.numeroFactura} onChange={e => {
              setFacturaError('');
              setFormData({
                ...formData,
                numeroFactura: e.target.value
              });
            }} placeholder="FAC-2024-001" data-id="element-2244" data-mp="2mufqsycpipws" />
              

              <div data-id="element-2245" data-mp="1hviwhwiy75nz">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2246" data-mp="1htya4u6set17">
                  Estado
                </label>
                <select value={formData.estado} onChange={e => {
                const newEstado = e.target.value as Invoice['estado'];
                setFormData({
                  ...formData,
                  estado: newEstado
                });
              }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2247" data-mp="18v4b6x5zhqkh">
                  
                  <option value="pendiente" data-id="element-2248" data-mp="b7c8jno5px5i">Pendiente</option>
                  <option value="parcial" data-id="element-2249" data-mp="b9yh9qmsaipb">Pago Parcial</option>
                  <option value="vencida" data-id="element-2250" data-mp="g5ducgcywako">Vencida</option>
                  <option value="pagada" data-id="element-2251" data-mp="3sq1agp3ya2s3">Pagada</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2252" data-mp="3nrzvogdk4pmf">
              <SearchableSelect label="Farmacia" value={formData.farmacia} onChange={val => setFormData({
              ...formData,
              farmacia: val
            })} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2253" data-mp="3ntaus2w25977" />
              

              <div data-id="element-2254" data-mp="ak542ixsntgx">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2255" data-mp="ailnssc51if5">
                  Vendedor
                </label>
                <select value={formData.vendedorId} onChange={e => {
                const selectedUser = users.find(u => u.id === e.target.value);
                setFormData({
                  ...formData,
                  vendedorId: e.target.value,
                  vendedor: selectedUser?.nombre || ''
                });
              }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2256" data-mp="k9y4qoweeo6e">
                  
                  <option value="" data-id="element-2257" data-mp="2emusveclku80">Seleccionar vendedor...</option>
                  {users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true).map(u => <option key={u.id} value={u.id} data-id="element-2258" data-mp="1voiixqdv3gbd">
                        {u.nombre}
                      </option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2259" data-mp="z4t9wqaefbnm">
              <Input label="Fecha de Emisión" type="date" value={formData.fechaEmision} onChange={e => setFormData({
              ...formData,
              fechaEmision: e.target.value
            })} data-id="element-2260" data-mp="136wp9q06v0e6" />
              

              <Input label="Fecha de Vencimiento" type="date" value={formData.fechaVencimiento} min={!editingInvoice ? new Date().toISOString().split('T')[0] : undefined} onChange={e => {
              const newFechaVenc = e.target.value;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const fechaVenc = new Date(newFechaVenc);
              const saldoPend = Number(formData.saldoPendiente);
              const monto = Number(formData.monto);
              let newEstado = formData.estado;
              if (formData.estado === 'vencida' && fechaVenc >= today) {
                // Fecha movida al futuro → ya no está vencida
                newEstado = saldoPend < monto && saldoPend > 0 ? 'parcial' : 'pendiente';
              } else if (formData.estado !== 'pagada' && fechaVenc < today && saldoPend > 0) {
                // Fecha movida al pasado → marcar como vencida
                newEstado = 'vencida';
              }
              setFormData({
                ...formData,
                fechaVencimiento: newFechaVenc,
                estado: newEstado
              });
            }} data-id="element-2261" data-mp="mq4qyhax437y" />
              
            </div>
            <div className="grid grid-cols-3 gap-4" data-id="element-2262" data-mp="1qulnuvlk51e0">
              <Input label="Sub Total (DOP)" type="number" value={formData.subTotal} onChange={e => {
              const sub = Number(e.target.value) || 0;
              const tax = Math.round(sub * 0.18 * 100) / 100;
              const total = Math.round((sub + tax) * 100) / 100;
              setFormData({
                ...formData,
                subTotal: e.target.value,
                itbis: String(tax),
                monto: String(total),
                saldoPendiente: editingInvoice ? formData.saldoPendiente : String(total)
              });
            }} placeholder="0" data-id="element-2263" data-mp="1qrz7c6govbjn" />
              

              <Input label="ITBIS 18% (DOP)" type="number" value={formData.itbis} onChange={e => {
              const tax = Number(e.target.value) || 0;
              const sub = Number(formData.subTotal) || 0;
              const total = Math.round((sub + tax) * 100) / 100;
              setFormData({
                ...formData,
                itbis: e.target.value,
                monto: String(total),
                saldoPendiente: editingInvoice ? formData.saldoPendiente : String(total)
              });
            }} placeholder="0" data-id="element-2264" data-mp="u2opq9k8c9xe" />
              

              <div data-id="element-2265" data-mp="3bqhzuazgp4x0">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2266" data-mp="3bpztummzzcbw">
                  Monto Total (DOP)
                </label>
                <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 font-bold" data-id="element-2267" data-mp="37oekvcdl3i6z">
                  {formatCurrency(Number(formData.monto) || 0)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2268" data-mp="3vdz7q6cuyzlj">
              <Input label="Saldo Pendiente (DOP)" type="number" value={formData.saldoPendiente} onChange={e => setFormData({
              ...formData,
              saldoPendiente: e.target.value
            })} placeholder="0" data-id="element-2269" data-mp="3vcebmcawb8lk" />
              

              <div data-id="element-2270" data-mp="3wubkt2qr4l8">
                <Input label="PP % (Pronto Pago)" type="number" value={formData.ppPct} onChange={e => setFormData({
                ...formData,
                ppPct: e.target.value
              })} placeholder={`Global: ${discountPct}%`} data-id="element-2271" data-mp="3xdqs210bevk" />
                

                <p className="text-xs text-slate-400 mt-1" data-id="element-2272" data-mp="3hafjm0tt205y">
                  Dejar vacío para usar el % global ({discountPct}%)
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2273" data-mp="3cdfo2jul0ft3">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-2274" data-mp="3cdyup4es7hrt">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.numeroFactura || !formData.monto || !formData.fechaVencimiento} data-id="element-2275" data-mp="jhxdikng201h">
            
            {editingInvoice ? 'Guardar Cambios' : 'Crear Factura'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Invoice Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Factura" size="sm" data-id="element-2276" data-mp="3w1ez9bixpv0m">
        
        <ModalBody data-id="element-2277" data-mp="3mzntk6oe9o6r">
          <p className="text-slate-600" data-id="element-2278" data-mp="3myvzx1ecy0j4">
            ¿Eliminar la factura{' '}
            <strong data-id="element-2279" data-mp="3i0uh8hkug386">{editingInvoice?.numeroFactura}</strong>? Esta acción no se
            puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-2280" data-mp="1l6zqbxbr30qz">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-2281" data-mp="1l7j7vcxhnn4h">
            
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-2282" data-mp="1ntik14cd65v5">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>

      {/* NC Add/Edit Modal */}
      <Modal isOpen={isNCModalOpen} onClose={() => setIsNCModalOpen(false)} title={editingNC ? 'Editar Nota de Crédito' : 'Nueva Nota de Crédito'} size="md" data-id="element-2283" data-mp="1xijyur76ah7j">
        
        <ModalBody data-id="element-2284" data-mp="14twa2ln7t9p8">
          <div className="space-y-4" data-id="element-2285" data-mp="14uebdwoqie40">
            {ncError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-2286" data-mp="14w907m34mqdd">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-2287" data-mp="1esc3wwdmmx2f" />
                {ncError}
              </div>}
            <Input label="Número de NC" value={ncFormData.numero} onChange={e => {
            setNCError('');
            setNCFormData({
              ...ncFormData,
              numero: e.target.value
            });
          }} placeholder="NC-00001" data-id="element-2288" data-mp="1jrx0x198kvci" />
            

            <SearchableSelect label="Farmacia" value={ncFormData.farmacia} onChange={val => setNCFormData({
            ...ncFormData,
            farmacia: val
          })} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2289" data-mp="q4yc34ojre4s" />
            

            <div data-id="element-2290" data-mp="ce303jir4gwz">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2291" data-mp="cbgxzzemqtm5">
                Vendedor
              </label>
              <select value={ncFormData.vendedorId} onChange={e => {
              const selectedUser = users.find(u => u.id === e.target.value);
              setNCFormData({
                ...ncFormData,
                vendedorId: e.target.value,
                vendedor: selectedUser?.nombre || ''
              });
            }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2292" data-mp="l5mww03twgpb">
                
                <option value="" data-id="element-2293" data-mp="30s1oziomkao9">Seleccionar vendedor...</option>
                {users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true).map(u => <option key={u.id} value={u.id} data-id="element-2294" data-mp="28blj4qooi8x">
                      {u.nombre}
                    </option>)}
              </select>
            </div>
            <Input label="Monto (DOP)" type="number" value={ncFormData.monto} onChange={e => setNCFormData({
            ...ncFormData,
            monto: e.target.value
          })} placeholder="0" data-id="element-2295" data-mp="3ogbg904lxqms" />
            

            <div data-id="element-2296" data-mp="1uby69g7fbdyt">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2297" data-mp="1ucg9wxwf0ugr">
                Notas
              </label>
              <textarea value={ncFormData.notas} onChange={e => setNCFormData({
              ...ncFormData,
              notas: e.target.value
            })} rows={2} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notas opcionales..." data-id="element-2298" data-mp="1peeoi6bioj24" />
              
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2299" data-mp="1zb1bp1rbfm7b">
          <Button variant="secondary" onClick={() => setIsNCModalOpen(false)} data-id="element-2300" data-mp="1z9gibfgqm9b1">
            Cancelar
          </Button>
          <Button onClick={handleSaveNC} isLoading={isSaving} disabled={!ncFormData.numero || !ncFormData.monto || !ncFormData.farmacia} data-id="element-2301" data-mp="2vtffdxloqb1u">
            
            {editingNC ? 'Guardar Cambios' : 'Crear NC'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* NC Delete Modal */}
      <Modal isOpen={isNCDeleteModalOpen} onClose={() => setIsNCDeleteModalOpen(false)} title="Eliminar Nota de Crédito" size="sm" data-id="element-2302" data-mp="miy4n1zphy6n">
        
        <ModalBody data-id="element-2303" data-mp="whnd645pq6a8">
          <p className="text-slate-600" data-id="element-2304" data-mp="wi5kpwxts6xg">
            ¿Eliminar la NC <strong data-id="element-2305" data-mp="wfjbztz77ldn">{editingNC?.numero}</strong>? Esta acción no
            se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-2306" data-mp="167p3z3pga2su">
          <Button variant="secondary" onClick={() => setIsNCDeleteModalOpen(false)} data-id="element-2307" data-mp="1676yrhkt8e8s">
            
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteNC} data-id="element-2308" data-mp="1f854q5et6vu0">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={isPaymentModalOpen} onClose={() => {
      setIsPaymentModalOpen(false);
      setPaymentDone(false);
    }} title={paymentDone ? '✓ Pago Registrado' : 'Registrar Pago Múltiple'} size="lg" data-id="element-2309" data-mp="1k7q24bebyw4m">
        
        <ModalBody data-id="element-2310" data-mp="2bifwhucc1c60">
          {paymentDone && lastPaymentData ? <div className="space-y-5" data-id="element-2311" data-mp="2bk0s7nadv15g">
              <div className="flex flex-col items-center text-center p-6 bg-emerald-50 border border-emerald-200 rounded-2xl" data-id="element-2312" data-mp="2gfzdh20f031n">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4" data-id="element-2313" data-mp="346v6u3kir6b0">
                  <CheckCircleIcon className="w-9 h-9 text-emerald-600" data-id="element-2314" data-mp="38a1d3b7msxhh" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2315" data-mp="2uga4c51q5hmt">
                  ¡Pago procesado exitosamente!
                </h3>
                <p className="text-sm text-slate-600" data-id="element-2316" data-mp="3n5hanq7f7bim">
                  Se registró un pago de{' '}
                  <strong className="text-emerald-700" data-id="element-2317" data-mp="3rwfu2ynpgpq3">
                    {formatCurrency(lastPaymentData.total)}
                  </strong>{' '}
                  para <strong data-id="element-2318" data-mp="53isgwgxtyis">{lastPaymentData.farmacia}</strong>
                </p>
                <div className="mt-3 flex flex-wrap gap-2 justify-center" data-id="element-2319" data-mp="12yvpu0a0wx6">
                  {lastPaymentData.invoices.map(inv => <span key={inv.numeroFactura} className="px-2.5 py-1 bg-white border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-700" data-id="element-2320" data-mp="ex99jgaqj6og">
                  
                      {inv.numeroFactura}: {formatCurrency(inv.abono)}
                    </span>)}
                </div>
              </div>
              <div data-id="element-2321" data-mp="3saun13kkiugk">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2322" data-mp="3s7yll59ihnnd">
                  Método de pago (para el recibo)
                </label>
                <select value={paymentMetodo} onChange={e => setPaymentMetodo(e.target.value)} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2323" data-mp="17j11k0ukzvjw">
                
                  <option value="efectivo" data-id="element-2324" data-mp="1elza76kwzmla">Efectivo</option>
                  <option value="cheque" data-id="element-2325" data-mp="1jm3qowltohbi">Cheque</option>
                  <option value="transferencia" data-id="element-2326" data-mp="1jmmz9mpl1tcv">Transferencia</option>
                </select>
              </div>
              <button onClick={handleDownloadRecibo} disabled={isDownloadingRecibo} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#2952C4] hover:bg-[#1E3D96] disabled:opacity-60 text-white font-bold text-base rounded-xl transition-colors shadow-lg" data-id="element-2327" data-mp="1okf0x8g14tu9">
              
                {isDownloadingRecibo ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-2328" data-mp="15ndster6idm3" /> : <DownloadIcon className="w-5 h-5" data-id="element-2329" data-mp="865dqhnixb50" />}
                {isDownloadingRecibo ? 'Generando PDF...' : 'Descargar Recibo PDF'}
              </button>
            </div> : <div className="space-y-6" data-id="element-2330" data-mp="5lqte83kvx8i">
              <SearchableSelect label="Seleccionar Cliente" value={paymentClient} onChange={val => {
            setPaymentClient(val);
            setPaymentAmounts({});
          }} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2331" data-mp="mw3km7718ew" />
            

              {paymentClient && (clientInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200" data-id="element-2332" data-mp="3e225dhcqziwf">
                    <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-2333" data-mp="394ae8pjxg0ta" />
                    <p className="text-slate-600 font-medium" data-id="element-2334" data-mp="344xj7hiwjnfm">
                      Este cliente no tiene facturas pendientes
                    </p>
                  </div> : <div className="space-y-4" data-id="element-2335" data-mp="2v3g7scgfw9x9">
                    <div className="flex items-center justify-between" data-id="element-2336" data-mp="2v4r6hxv12rhi">
                      <h3 className="text-sm font-semibold text-slate-900" data-id="element-2337" data-mp="3jtu5xphwvv8m">
                        Facturas Pendientes
                      </h3>
                      <span className="text-xs text-slate-500" data-id="element-2338" data-mp="3osfb83c0t53x">
                        Ingrese el monto a pagar por factura
                      </span>
                    </div>
                    <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2339" data-mp="3f22n614zytt1">
                      <table className="w-full text-sm" data-id="element-2340" data-mp="312unl74x9job">
                        <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2341" data-mp="362paz0phnr4m">
                          <tr data-id="element-2342" data-mp="360cny94viqkp">
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-2343" data-mp="2s3fvy4d0r328">
                              Factura
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-2344" data-mp="2w6clkf0z7zfo">
                              Vencimiento
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500" data-id="element-2345" data-mp="ucms2tyqwixl">
                              Saldo
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500 w-40" data-id="element-2346" data-mp="2au5a6glivae3">
                              Abono
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-2347" data-mp="2jtie6tqvn6rf">
                          {clientInvoices.map(inv => <tr key={inv.id} className="hover:bg-slate-50/50" data-id="element-2348" data-mp="2yhd82dgmfh63">
                              <td className="px-4 py-3 font-medium text-slate-900" data-id="element-2349" data-mp="2tjldtcsi7iyq">
                                {inv.numeroFactura}
                              </td>
                              <td className="px-4 py-3 text-slate-600" data-id="element-2350" data-mp="32kjjdzilbyjf">
                                {formatDate(inv.fechaVencimiento)}
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900" data-id="element-2351" data-mp="186s4akhhsmgl">
                                {formatCurrency(inv.saldoPendiente)}
                              </td>
                              <td className="px-4 py-3" data-id="element-2352" data-mp="3d836zz3b7xe2">
                                <div className="relative" data-id="element-2353" data-mp="3d8mlt6xny5nv">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-2354" data-mp="3i6o61v6tsb0x">
                                    $
                                  </span>
                                  <input type="number" min="0" max={inv.saldoPendiente} placeholder="0" value={paymentAmounts[inv.id] || ''} onChange={e => handlePaymentAmountChange(inv.id, e.target.value)} className="w-full pl-6 pr-3 py-1.5 text-right border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900" data-id="element-2355" data-mp="3r85h2z5dlmir" />
                          
                                </div>
                              </td>
                            </tr>)}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-2356" data-mp="1pabutfesiwkv">
                          <tr data-id="element-2357" data-mp="1pceqaeypphwz">
                            <td colSpan={3} className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-2358" data-mp="2m14bof9jssnp">
                        
                              Total a Pagar:
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-600 text-lg" data-id="element-2359" data-mp="2d6g94wvcxp2l">
                              {formatCurrency(totalPayment)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>)}
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-2360" data-mp="298pipjhv3im8">
          {paymentDone ? <Button variant="secondary" onClick={() => {
          setIsPaymentModalOpen(false);
          setPaymentDone(false);
        }} data-id="element-2361" data-mp="2e0qwj9uohhed">
            
              Cerrar
            </Button> : <>
              <Button variant="secondary" onClick={() => setIsPaymentModalOpen(false)} data-id="element-2362" data-mp="24bqvzm0j2dy2">
              
                Cancelar
              </Button>
              <Button onClick={handleProcessPayment} isLoading={isSaving} disabled={!paymentClient || totalPayment <= 0} data-id="element-2363" data-mp="2wytljon87ec6">
              
                Procesar Pago
              </Button>
            </>}
        </ModalFooter>
      </Modal>

      {/* Cotizacion Selection Modal */}
      <Modal isOpen={isCotizacionModalOpen} onClose={() => setIsCotizacionModalOpen(false)} title="Cotizaciones" size="lg" backdropBlur stacked data-id="element-2364" data-mp="17n1allpoz37c">
        
        <ModalBody data-id="element-2365" data-mp="1453ty6vcw5up">
          <div className="space-y-4" data-id="element-2366" data-mp="z3xxbxykryfw">
            <div className="relative" data-id="element-2367" data-mp="z2wf99j56boo">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2368" data-mp="z60rmu6rgdqf" />
              <input type="text" placeholder="Buscar por número o farmacia..." value={cotizacionSearch} onChange={e => setCotizacionSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-2369" data-mp="1dyuej0od3w54" />
              
            </div>
            {isLoadingCotizaciones ? <div className="py-10 text-center" data-id="element-2370" data-mp="qa1g8xk9pc7c">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-slate-100 rounded-xl mb-3" data-id="element-2371" data-mp="2lryq7u37am0">
                  <FileTextIcon className="w-5 h-5 text-slate-400 animate-spin" data-id="element-2372" data-mp="3tsurv4thjqyv" />
                </div>
                <p className="text-sm text-slate-500" data-id="element-2373" data-mp="1ft9jzs5sccxn">
                  Cargando cotizaciones...
                </p>
              </div> : <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-[400px] overflow-y-auto" data-id="element-2374" data-mp="1awsw8z2i00l3">
                <table className="w-full" data-id="element-2375" data-mp="1ooh8d2cqyp22">
                  <thead className="sticky top-0" data-id="element-2376" data-mp="1jveawj4umvgl">
                    <tr className="bg-slate-50 border-b border-slate-200" data-id="element-2377" data-mp="1jtjo0za0ot9v">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2378" data-mp="1yizcu9pzmomx">
                        Número
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2379" data-mp="27lqpudyihsgo">
                        Farmacia
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2380" data-mp="23hsso8sqyrq9">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2381" data-mp="96xhqzu3b0kb">
                        Total
                      </th>
                      <th className="px-4 py-3 w-20" data-id="element-2382" data-mp="33slnvcszags"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-2383" data-mp="3lhs0yixn9byh">
                    {cotizaciones.filter(c => (c.numero_cotizacion || '').toLowerCase().includes(cotizacionSearch.toLowerCase()) || (c.farmacia || '').toLowerCase().includes(cotizacionSearch.toLowerCase())).map(cotizacion => <tr key={cotizacion.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => {
                  // Import cotizacion data into the invoice form
                  setFormData(prev => ({
                    ...prev,
                    numeroFactura: cotizacion.numero_cotizacion || '',
                    farmacia: cotizacion.farmacia || '',
                    vendedor: cotizacion.vendedor || '',
                    monto: String(cotizacion.total || 0),
                    saldoPendiente: String(cotizacion.total || 0)
                  }));
                  setIsCotizacionModalOpen(false);
                  setCotizacionSearch('');
                }} data-id="element-2384" data-mp="37gz802mzqoxh">
                    
                          <td className="px-4 py-3 font-medium text-slate-900 text-sm" data-id="element-2385" data-mp="1i0vj1i43u6w1">
                            {cotizacion.numero_cotizacion}
                          </td>
                          <td className="px-4 py-3 text-slate-700 text-sm" data-id="element-2386" data-mp="189itix5s3st6">
                            {cotizacion.farmacia}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm" data-id="element-2387" data-mp="20z7vbcqbbwvu">
                            {cotizacion.fecha ? formatDate(new Date(cotizacion.fecha)) : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900 text-sm" data-id="element-2388" data-mp="2da8jykciyevq">
                            {formatCurrency(cotizacion.total || 0)}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-2389" data-mp="2n8el2zqibgzj">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-600" data-id="element-2390" data-mp="2r9i39sw11p3x">
                              Seleccionar
                            </span>
                          </td>
                        </tr>)}
                    {cotizaciones.filter(c => (c.numero_cotizacion || '').toLowerCase().includes(cotizacionSearch.toLowerCase()) || (c.farmacia || '').toLowerCase().includes(cotizacionSearch.toLowerCase())).length === 0 && <tr data-id="element-2391" data-mp="bj85hc1rgcn6">
                        <td colSpan={5} className="px-4 py-10 text-center" data-id="element-2392" data-mp="bk012mvcyadg">
                          <div className="inline-flex items-center justify-center w-10 h-10 bg-slate-100 rounded-xl mb-2" data-id="element-2393" data-mp="palj69czmgaa">
                            <FileTextIcon className="w-5 h-5 text-slate-400" data-id="element-2394" data-mp="l8rpwm54gek5" />
                          </div>
                          <p className="text-sm text-slate-500" data-id="element-2395" data-mp="3tpussswy19s0">
                            {cotizacionSearch ? 'No se encontraron cotizaciones' : 'No hay cotizaciones disponibles'}
                          </p>
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>}
          </div>
        </ModalBody>
      </Modal>

      {/* Estado de Cuenta Modal */}
      <Modal isOpen={showEstadoCuenta} onClose={() => setShowEstadoCuenta(false)} title="Estado de Cuenta por Cliente" data-id="element-2396" data-mp="1cz5xk0mjkund">
        
        <ModalBody data-id="element-2397" data-mp="1ljkk23pvhzdb">
          <div className="space-y-4" data-id="element-2398" data-mp="1pjb8w1vua5f7">
            <SearchableSelect label="Cliente (Farmacia)" options={FARMACIAS.map(f => ({
            value: f,
            label: f
          }))} value={estadoCuentaClient} onChange={setEstadoCuentaClient} placeholder="Seleccionar cliente..." data-id="element-2399" data-mp="1pkcx781v8oev" />
            

            {(dateFrom || dateTo) && <div className="p-3 bg-blue-50 text-blue-700 rounded-xl text-sm" data-id="element-2400" data-mp="295r3dm4qz1iu">
                <p className="font-medium mb-1" data-id="element-2401" data-mp="1uief3pnk2hor">Filtro de fechas activo:</p>
                <p data-id="element-2402" data-mp="1zgzgvtigh9fb">
                  {dateFrom ? formatDate(new Date(dateFrom + 'T00:00:00')) : 'Inicio'}{' '}
                  al{' '}
                  {dateTo ? formatDate(new Date(dateTo + 'T00:00:00')) : 'Hoy'}
                </p>
                <p className="text-xs mt-1 opacity-80" data-id="element-2403" data-mp="2ryl9dqke7jax">
                  El PDF solo incluirá las facturas emitidas en este rango.
                </p>
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2404" data-mp="2ws37sjqxt2n1">
          <Button variant="secondary" onClick={() => setShowEstadoCuenta(false)} data-id="element-2405" data-mp="2rut5w4md2yib">
            
            Cancelar
          </Button>
          <Button onClick={generateEstadoCuenta} isLoading={isGeneratingEstadoCuenta} disabled={!estadoCuentaClient} data-id="element-2406" data-mp="2nt7ygysljcfi">
            
            Generar PDF
          </Button>
        </ModalFooter>
      </Modal>

      {/* Discount Config Modal */}
      {canSeeDiscount && <Modal isOpen={isDiscountConfigOpen} onClose={() => setIsDiscountConfigOpen(false)} title="Configurar Descuento por Pronto Pago" size="sm" data-id="element-2407" data-mp="292ik5nju9h7f">
        
          <ModalBody data-id="element-2408" data-mp="2033v8sjblep1">
            <div className="space-y-4" data-id="element-2409" data-mp="205q0gliqni43">
              <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-800" data-id="element-2410" data-mp="1v4k7qnp2vuuk">
                El descuento se aplica automáticamente al usar "Aplicar NC".
              </div>
              <div data-id="element-2411" data-mp="fktef6las0qi">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2412" data-mp="fkb9zmoheg7i">
                  Porcentaje de descuento (%)
                </label>
                <input type="number" min="0.1" max="50" step="0.5" value={discountConfigDraft.pct} onChange={e => setDiscountConfigDraft({
              ...discountConfigDraft,
              pct: Number(e.target.value)
            })} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2413" data-mp="on2mlpt3fi0q" />
              
              </div>
              <div data-id="element-2414" data-mp="34bm4fst3sue3">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2415" data-mp="39crypv2ewppq">
                  Días máximos desde emisión
                </label>
                <input type="number" min="1" max="365" value={discountConfigDraft.days} onChange={e => setDiscountConfigDraft({
              ...discountConfigDraft,
              days: Number(e.target.value)
            })} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2416" data-mp="22pzdtbvhohel" />
              
              </div>
              <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-600 text-center" data-id="element-2417" data-mp="2gne56m5ayz97">
                Descuento de{' '}
                <strong className="text-emerald-700" data-id="element-2418" data-mp="2qeibyyktrdsy">
                  {discountConfigDraft.pct}%
                </strong>{' '}
                para cobros antes de los{' '}
                <strong className="text-emerald-700" data-id="element-2419" data-mp="2zfq8bb3s64ke">
                  {discountConfigDraft.days} días
                </strong>
              </div>
            </div>
          </ModalBody>
          <ModalFooter data-id="element-2420" data-mp="3izff1r6mbqjh">
            <Button variant="secondary" onClick={() => setIsDiscountConfigOpen(false)} data-id="element-2421" data-mp="3o0cqzm11bocj">
            
              Cancelar
            </Button>
            <Button onClick={() => {
          saveDiscountConfig(discountConfigDraft.pct, discountConfigDraft.days);
          addActivityLog('Editó descuento pronto pago', `Porcentaje: ${discountConfigDraft.pct}% | Días: ${discountConfigDraft.days}`);
          setIsDiscountConfigOpen(false);
        }} data-id="element-2422" data-mp="3a2wje7705oj7">
            
              Guardar Configuración
            </Button>
          </ModalFooter>
        </Modal>}
    </div>;
}